// Potidaea mockup — Phase 1 wire.
// Adds WizardConnect connection + live chipnet balance to the hand-authored HTML
// mockup at app.html. The existing inline JS (asset picker, wizard, review sheet,
// AMM rendering) is untouched; we hook in through:
//   1. A global window.potidaeaWc handle exposing the WC session snapshot
//   2. Direct DOM mutation of the wallet chip + slot markers this file inserts
//   3. Global window.ASSETS mutation to overwrite bal on `paired` — the inline
//      JS's recalc()/renderKat() already picks up mutated ASSETS on next input.
//
// Nothing broadcasts in Phase 1. The Swap "Confirm" / Artemision "Broadcast" /
// Publish "Publish" buttons still open their mock flows; a small banner is
// added to each so the visitor understands the visual outcome is illustrative
// and Phase 2 will wire the actual signing round-trip via WizardConnect.

import './wc.css';
import { ElectrumClient, fetchAddressUtxos, summariseBalance } from './electrum';
import { WcClient } from './wc';
import QRCode from 'qrcode';
import { redeemViaLocalSigner, KNOWN_MBAS, mergeKnownMbas, previewClaim } from './orbital';
import { wireDeaYield } from './deaYield';
import {
  claimViaWcWithRetry, claimViaLocalSignerWithRetry, claimViaLocalSignerShareWithRetry,
} from './retry';
import { genesisArtemisionMBA, genesisDeviantShareMBA, loadLocalKnownMbas, clearAllLocalAssets, type GenesisResult, type ShareGenesisResult, type ShareGenesisParams } from './genesis';
import { publishViaLocalSigner, PublishError, TEMPLATE_ARTEMISION_MINT_FT, TEMPLATE_DEVIANT_SHARE_MBA, type PublishMetadata } from './publish';
import { uploadIcon, IconUploadError } from './icon-upload';
import { bchUsd, refreshBchUsd } from './pricing';
import { BCH_ICON_DATA_URI } from './icons';
import { readKatalogos, fetchEntryParams, discoveredParamsToKnownMba, fetchAndVerifyIcon, type RegisteredEntry } from './registry';
import { decodeCashAddress } from '@bitauth/libauth';
import {
  loadActiveSigner, importUserWif, createUserWallet, clearUserWallet, readUserWif,
  listUserWallets, switchUserWallet, removeUserWallet, renameUserWallet,
  pinDevWif, clearDevWif, type Signer,
} from './signer-store';

// ── known chipnet CashToken categories the mockup asks the chain about ──────
// Ticker → hex category id. Only tickers present in the mockup's ASSETS array
// and holding a real chipnet category can have their bal replaced with live
// numbers; unknown ones keep whatever was hard-coded (or stay 0 for demo).
// Kept in sync with shells/app/src/core/wallet.ts CHIPNET_DEMO_TOKENS.
const KNOWN_CATEGORIES: Readonly<Record<string, string>> = {
  ASM:  '09af8a5b2fc2c9a2449045a8d5a6aa60db493989682eca257a57fa8d75d67231',
  ASM2: '8a9f592d4a3bdbc1bc23e57c151434cbebb79bc3a00a07e7f457c9ae8fea2815',
  CAP:  '155cdb786c2a34d79db937659d76e74459d9535b2a8c59661dcf5a6e82bb4a9d',
  // Chipnet DEA — the fee token behind KatalogosRegistryV1 (see publish.ts
  // CHIPNET_REGISTRY.feeCategoryId). Adding it here surfaces the balance in
  // the wallet and lets the base-currency chip strip route through DEA
  // pairs (the actual DEA→MBA swap covenant lives elsewhere — Potidaea's
  // KNOWN_MBAS is BCH-family only for now, so the picker will treat DEA
  // pairs as view-only until the DEA swap path lands).
  DEA:  '3c2337f504ad543518040c2d96477e473efaf8803aa010ce63e93c07996fd0c0',
};

// ── mockup <-> Vite glue ────────────────────────────────────────────────────
// The inline JS in app.html declares window.ASSETS + window.recalc + window.renderKat
// implicitly (as `var` at script-top scope). We read + mutate them here through
// the window handle; TypeScript's `unknown` cast avoids leaking a fat type surface.

interface MockAsset { sym: string; name: string; glyph: string; rate: number; cls: string; bal: number; pub?: boolean; iconUrl?: string; }
interface WindowExt {
  ASSETS?: MockAsset[];
  recalc?: () => void;
  renderKat?: () => void;
  potidaeaWc?: PotidaeaWcHandle;
  // Mockup swap-card state (declared via `var` in inline JS — visible on window)
  from?: string;
  to?: string;
}
declare global { interface Window extends WindowExt {} }

/** Public handle the inline JS (or a future Phase 2 signing wire) can call. */
export interface PotidaeaWcHandle {
  connect(): void;
  disconnect(): void;
  refreshBalance(): Promise<void>;
  readonly cashaddr: string | null;
}

const wc = new WcClient({
  network: 'chipnet',
  dappName: 'Potidaea',
  dappIcon: undefined,   // TODO: 128×128 favicon-alike data-URL
});

// ── UI hookup — the wallet chip in the topbar ───────────────────────────────
// The existing HTML holds a fake chip: <div class="wallet-chip"> with a dot,
// short address, and BCH figure. We replace its contents on every WC state
// change. The class stays so the surrounding CSS keeps working.
function chipEl(): HTMLElement | null { return document.querySelector('.wallet-chip'); }

function renderChip(): void {
  const el = chipEl();
  if (!el) return;
  paintWalletMenu();
  // Local signer (user or dev) takes precedence: paint chip as attached.
  if (localSigner) { renderChipLocal(el, localSigner); return; }
  const snap = wc.snapshot;
  el.classList.toggle('wc-connected', snap.phase === 'paired');
  el.classList.toggle('wc-idle', snap.phase === 'idle');
  el.classList.toggle('wc-connecting', snap.phase === 'awaiting-scan');
  el.classList.toggle('wc-error', snap.phase === 'error');
  if (snap.phase === 'paired' && snap.cashaddr) {
    const addr = snap.cashaddr;
    const short = addr.slice(0, 14) + '…' + addr.slice(-6);
    el.innerHTML =
      '<span class="wc-dot"></span>' +
      '<span class="wc-addr" title="' + addr + '">' + short + '</span>' +
      ' · <b id="chipBch">…</b>' +
      ' <span class="chev">▾</span>';
  } else if (snap.phase === 'awaiting-scan') {
    el.innerHTML = '<span class="wc-dot"></span><span class="wc-addr">connecting…</span> <span class="chev">▾</span>';
  } else {
    // Idle — keep the chip a single click target; the click opens #walletMenu
    // via the mockup's own coordinated toggle (netMenu ↔ walletMenu mutex,
    // outside-click closes). Do NOT inject a nested <button> here — that
    // would swallow the outer chip's click handler.
    el.innerHTML = '<span class="wc-dot"></span><span class="wc-addr">Attach wallet</span> <span class="chev">▾</span>';
  }
}

function renderChipLocal(el: HTMLElement, s: Signer): void {
  el.classList.remove('wc-idle', 'wc-connecting', 'wc-error');
  el.classList.add('wc-connected');
  const addr = s.cashaddr;
  const short = addr.slice(0, 14) + '…' + addr.slice(-6);
  const badgeTxt = s.mode === 'dev' ? 'dev' : 'local';
  const badgeTitle = s.mode === 'dev'
    ? 'dev signer · localhost only · pinned via console'
    : 'key held by this page (not a paired wallet)';
  el.innerHTML =
    '<span class="wc-dot"></span>' +
    '<span class="wc-addr" title="' + addr + ' (' + badgeTitle + ')">' + short + '</span>' +
    ' · <b id="chipBch">…</b>' +
    ' <span class="wc-mode-badge" title="' + badgeTitle + '">' + badgeTxt + '</span>' +
    ' <span class="chev">▾</span>';
}

function detachLocalSigner(): void {
  if (!localSigner) return;
  if (localSigner.mode === 'user') {
    // Chip ✕ = remove just this one. Other stored wallets stay put and one
    // of them will take over automatically. Same UX as clicking ✕ on the
    // active row of the wallet menu.
    removeStoredWallet(localSigner.cashaddr);
    return;
  }
  // dev mode: single-slot, no siblings — clear the pin
  clearDevWif();
  localSigner = null;
  renderChip();
  paintWalletMenu();
  zeroAllBalances();
}

// ── QR/URI overlay — shown while awaiting-scan ──────────────────────────────
// Full WizardConnect pairing surface per docs.riftenlabs.com/wizardconnect:
// - Scannable QR of the wiz:// URI (dense but readable at ~240×240 px)
// - "Open in wallet" deep-link button — for same-device users the wiz:// scheme
//   auto-opens their paired app instead of forcing a QR round-trip
// - Copy-to-clipboard fallback for anything else
// The scrim is rebuilt idempotently as the snapshot changes; the QR renders
// asynchronously so the modal appears instantly even before the SVG is ready.
function scrimEl(): HTMLElement | null { return document.getElementById('wc-scrim'); }
function ensureScrim(): HTMLElement {
  let el = scrimEl();
  if (el) return el;
  el = document.createElement('div'); el.id = 'wc-scrim';
  el.className = 'wc-scrim';
  el.innerHTML =
    '<div class="wc-modal">' +
      '<div class="wc-modal-h">Connect a WizardConnect wallet</div>' +
      '<p class="wc-modal-sub">Scan the QR with your wallet, tap <b>Open in wallet</b> if it lives on this device, or copy the URI and paste it in the wallet\'s Connect screen.</p>' +
      '<div class="wc-qr" id="wc-qr"><div class="wc-qr-loading">Generating QR…</div></div>' +
      '<div class="wc-uri-box"><code id="wc-uri">…</code></div>' +
      '<div class="wc-actions">' +
        '<a class="wc-primary wc-open" id="wc-open" href="#" target="_blank" rel="noopener noreferrer">Open in wallet ↗</a>' +
        '<button class="wc-copy">Copy URI</button>' +
        '<button class="wc-cancel">Cancel</button>' +
      '</div>' +
      '<div class="wc-hint">First-time users: install any WizardConnect-compatible BCH wallet. Nothing on this page ever holds your keys — the pairing is <a href="https://docs.riftenlabs.com/wizardconnect/" target="_blank" rel="noopener noreferrer" style="color:var(--acid)">Nostr-relayed</a>, so no server sees your address either.</div>' +
    '</div>';
  document.body.appendChild(el);
  el.addEventListener('click', (ev) => { if (ev.target === el) wc.disconnect(); });
  el.querySelector('.wc-cancel')?.addEventListener('click', () => wc.disconnect());
  el.querySelector('.wc-copy')?.addEventListener('click', async () => {
    const uri = wc.snapshot.uri; if (!uri) return;
    try { await navigator.clipboard.writeText(uri); } catch { /* clipboard blocked; user selects manually */ }
    const btn = el!.querySelector('.wc-copy') as HTMLButtonElement | null; if (btn) { btn.textContent = 'Copied'; setTimeout(() => { btn.textContent = 'Copy URI'; }, 1500); }
  });
  return el;
}

// Track the URI we last rendered a QR for so a snapshot repaint doesn't
// blow away the SVG unnecessarily.
let lastRenderedQrUri: string | null = null;

function renderScrim(): void {
  const snap = wc.snapshot;
  if (snap.phase !== 'awaiting-scan' || !snap.uri) {
    scrimEl()?.remove();
    lastRenderedQrUri = null;
    return;
  }
  const el = ensureScrim();
  const code = el.querySelector('#wc-uri') as HTMLElement | null;
  if (code) code.textContent = snap.uri;
  const openLink = el.querySelector('#wc-open') as HTMLAnchorElement | null;
  if (openLink) openLink.href = snap.uri;
  // Render QR only when the URI actually changed — avoids flicker on
  // repeated `change` emissions.
  const qrHost = el.querySelector('#wc-qr') as HTMLElement | null;
  if (qrHost && snap.uri !== lastRenderedQrUri) {
    lastRenderedQrUri = snap.uri;
    // qrcode.toString returns SVG we can inline directly. errorCorrectionLevel
    // 'L' keeps the QR density low for short wiz:// URIs; margin 1 keeps the
    // quiet zone visible without wasting space. Colors match the acid+ink
    // palette so it reads on the dark modal background.
    QRCode.toString(snap.uri, {
      type: 'svg',
      errorCorrectionLevel: 'L',
      margin: 1,
      color: { dark: '#d6ff3d', light: '#0a0d16' },
    }).then((svg) => {
      // Guard against stale writes: only paint if the URI is still current.
      if (wc.snapshot.uri === snap.uri) qrHost.innerHTML = svg;
    }).catch((e) => {
      console.warn('[wc] QR render failed:', e instanceof Error ? e.message : e);
      qrHost.innerHTML = '<div class="wc-qr-loading">QR unavailable — use the URI below</div>';
    });
  }
}

// ── Balance refresh — on `paired`, hit chipnet electrum ──────────────────────
let currentClient: ElectrumClient | null = null;

async function refreshBalance(): Promise<void> {
  // Dev-signer takes precedence: when localhost + WIF pinned, refresh against
  // its cashaddr regardless of WC state. Fall back to WC snapshot otherwise.
  const cashaddr = localSigner?.cashaddr ?? (wc.snapshot.phase === 'paired' ? wc.snapshot.cashaddr : null);
  if (!cashaddr) return;
  if (!currentClient) {
    try { currentClient = await ElectrumClient.connectAny(); }
    catch { paintBalanceErr('offline'); return; }
  }
  try {
    // Reconcile txid-based confirmation FIRST so a stale reap doesn't
    // drop entries the endpoint has actually seen. Then fetch UTXOs.
    await reconcilePendingViaTxid();
    const utxos = await fetchAddressUtxos(currentClient, cashaddr);
    console.log('[potidaea] balance for', cashaddr, '→', utxos.length, 'utxos');
    const bal = summariseBalance(utxos);
    paintBalance(bal.bchSats, bal.ftByCategory);
    // Piggyback a DEA-yield tab refresh — the user's DEA holdings just
    // changed, so their pro-rata share may have too. Fire-and-forget.
    interface DeaYieldHandle { refresh(): Promise<void> }
    const y = (window as unknown as { potidaeaDeaYield?: DeaYieldHandle }).potidaeaDeaYield;
    if (y) void y.refresh();
  } catch (e) {
    paintBalanceErr(e instanceof Error ? e.message : 'error');
  }
}

// Pending-swap layer — see paintBalance for why this matters.
//
// The problem it solves: right after we broadcast a swap tx, the very
// next refreshBalance() call races the electrum server's mempool
// indexing. In practice the endpoint returns the PRE-swap UTXO set for
// a second or two — paintBalance then writes the OLD BCH balance back
// onto the row and the chip snaps back up. Even follow-up refreshes at
// 500ms/3s can lose this race depending on the endpoint.
//
// Fix: applyOptimisticSwapDelta pushes a { txid, bchDeltaSats, ticker,
// tokenDeltaUnits } entry here. paintBalance then OVERLAYS these deltas
// on top of the electrum reading — subtract from BCH, add to the token —
// until we can tell the swap is settled at the endpoint. Detection: if
// the endpoint's reported BCH is already below the pre-swap snapshot,
// the swap has landed → drop the pending. A 60s hard timeout catches
// cases where the wallet gets other movement in between.
interface PendingSwap {
  txid: string;
  ticker: string;
  bchDeltaSats: bigint;   // subtract from BCH while pending
  tokenDeltaUnits: bigint; // add to `ticker` while pending
  preSwapBchSats: bigint; // BCH balance snapshot AT broadcast
  ts: number;
  /** Non-null once we've confirmed via `blockchain.transaction.get` that
   *  the tx is at least in the endpoint's mempool. Once confirmed, the
   *  entry can be safely cleared without needing the "moved" heuristic. */
  confirmed?: boolean;
}
const pendingSwaps: PendingSwap[] = [];
/** Persist pending swaps across page reloads so a browser refresh
 *  mid-flight doesn't lose the overlay while electrum is still catching
 *  up. Small JSON blob keyed to the wallet address. */
const PENDING_SWAPS_KEY = 'deviant.potidaea.pending-swaps.v1';

function loadPendingSwapsFromStorage(): void {
  try {
    const raw = localStorage.getItem(PENDING_SWAPS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Array<Record<string, unknown>>;
    if (!Array.isArray(parsed)) return;
    for (const r of parsed) {
      pendingSwaps.push({
        txid: String(r.txid ?? ''),
        ticker: String(r.ticker ?? ''),
        bchDeltaSats: BigInt(String(r.bchDeltaSats ?? '0')),
        tokenDeltaUnits: BigInt(String(r.tokenDeltaUnits ?? '0')),
        preSwapBchSats: BigInt(String(r.preSwapBchSats ?? '0')),
        ts: Number(r.ts ?? Date.now()),
        confirmed: r.confirmed === true ? true : undefined,
      });
    }
  } catch { /* ignore corrupt storage */ }
}

function savePendingSwapsToStorage(): void {
  try {
    const flat = pendingSwaps.map((p) => ({
      txid: p.txid, ticker: p.ticker,
      bchDeltaSats: p.bchDeltaSats.toString(),
      tokenDeltaUnits: p.tokenDeltaUnits.toString(),
      preSwapBchSats: p.preSwapBchSats.toString(),
      ts: p.ts, confirmed: p.confirmed,
    }));
    localStorage.setItem(PENDING_SWAPS_KEY, JSON.stringify(flat));
  } catch { /* private mode */ }
}

function currentBchSatsFromAssets(): bigint {
  const bchRow = window.ASSETS?.find((a) => a.sym === 'BCH');
  const bch = bchRow?.bal ?? 0;
  return BigInt(Math.round(bch * 1e8));
}

function registerPendingSwap(p: Omit<PendingSwap, 'ts' | 'preSwapBchSats'> & { preSwapBchSats: bigint }): void {
  pendingSwaps.push({ ...p, ts: Date.now() });
  savePendingSwapsToStorage();
}

/** Hard-timeout for pending overlay entries. 30 minutes is well over
 *  chipnet's typical mempool inclusion + first-confirmation latency,
 *  so an entry only ages out when something has gone seriously wrong.
 *  Below this ceiling, entries clear either through the reap "moved"
 *  heuristic (electrum-reported BCH shifted in the expected direction)
 *  or through explicit tx-confirmation (see reconcilePendingViaTxid). */
const PENDING_HARD_TIMEOUT_MS = 30 * 60 * 1000;

/** Filter out settled or expired pending swaps and return what's still
 *  in flight. The ONLY reliable "settled" signal is the endpoint's own
 *  UTXO set moving in the expected direction:
 *    • claim  (bchDelta > 0, BCH decreases): reported < preSwap
 *    • redeem (bchDelta < 0, BCH increases): reported > preSwap
 *
 *  Earlier versions also dropped entries on the strength of a
 *  `blockchain.transaction.get` hit — but that call can succeed on a
 *  chipnet Fulcrum whose `listunspent` hasn't caught up to the same
 *  mempool tx yet. Dropping the overlay before the UTXO set reflects
 *  the swap causes bchRow.bal to snap back to the pre-swap value,
 *  which is precisely the "returned me more BCH than I started with"
 *  bug when a redeem follows: it reads the inflated basis and adds
 *  the released BCH on top. Now `confirmed` is stored for diagnostics
 *  only; the overlay stays live until UTXOs move or the hard timeout
 *  expires. */
function reapPendingSwaps(reportedBchSats: bigint): PendingSwap[] {
  const now = Date.now();
  const alive: PendingSwap[] = [];
  for (const p of pendingSwaps) {
    if (now - p.ts > PENDING_HARD_TIMEOUT_MS) continue;       // hard timeout
    const moved = p.preSwapBchSats - reportedBchSats; // + = BCH went down; − = BCH went up
    const expected = p.bchDeltaSats;                 // + = expected to decrease; − = expected to increase
    // Same-sign move = endpoint caught up (settled). Different sign or
    // zero move = still stale, keep overlay live.
    if ((expected > 0n && moved > 0n) || (expected < 0n && moved < 0n)) continue;
    alive.push(p);
  }
  const changed = alive.length !== pendingSwaps.length;
  pendingSwaps.length = 0;
  pendingSwaps.push(...alive);
  if (changed) savePendingSwapsToStorage();
  return alive;
}

/** Ask the endpoint whether each pending tx is at least in its mempool.
 *  If yes, mark that pending as confirmed — then the next reap will drop
 *  it whether or not the reported UTXO set has caught up. Failures are
 *  swallowed (offline / txid-not-yet-broadcast is fine, we'll retry). */
async function reconcilePendingViaTxid(): Promise<void> {
  if (!currentClient) return;
  const client = currentClient;
  const unconfirmed = pendingSwaps.filter((p) => !p.confirmed);
  if (!unconfirmed.length) return;
  await Promise.all(unconfirmed.map(async (p) => {
    try {
      const hex = await client.request<string>('blockchain.transaction.get', [p.txid, false]);
      if (typeof hex === 'string' && hex.length > 0) {
        p.confirmed = true;
        savePendingSwapsToStorage();
      }
    } catch {
      // Tx not indexed here yet — try again on the next refresh.
    }
  }));
}

function paintBalance(bchSats: bigint, ftByCategory: Map<string, bigint>): void {
  const assets = window.ASSETS;
  if (!assets) return;

  // Apply pending-swap overlay — this makes the just-broadcast decrement
  // stick until the electrum endpoint catches up to it.
  const pending = reapPendingSwaps(bchSats);
  let effectiveBchSats = bchSats;
  for (const p of pending) {
    effectiveBchSats = effectiveBchSats > p.bchDeltaSats ? effectiveBchSats - p.bchDeltaSats : 0n;
  }
  const bch = Number(effectiveBchSats) / 1e8;
  const bchEl = document.getElementById('chipBch');
  if (bchEl) bchEl.textContent = bch.toFixed(4) + ' BCH';

  // Categories we can source from chipnet in this build:
  //   • KNOWN_CATEGORIES — hand-picked seeds (BCH·ASM·ASM2·CAP·DEA)
  //   • KNOWN_MBAS       — everything the user genesised locally or that
  //                        was discovered via KatalogosRegistryV1
  // Everything outside is a fixture-only ticker whose stored `bal` would
  // lie to the user (real wallet holds 0).
  const catByTicker = new Map<string, string>();
  for (const [sym, cat] of Object.entries(KNOWN_CATEGORIES)) catByTicker.set(sym, cat);
  for (const [sym, m] of Object.entries(KNOWN_MBAS)) catByTicker.set(sym, m.categoryId);
  const RESOLVED = new Set<string>(['BCH', ...catByTicker.keys()]);

  const bchRow = assets.find((a) => a.sym === 'BCH');
  if (bchRow) bchRow.bal = bch;

  // Sum pending token deltas by ticker so the MBA row also stays lifted
  // (a claim's freshly-received FTs may not be in the electrum snapshot
  // yet, mirroring the BCH-out side).
  const tokenBonus = new Map<string, bigint>();
  for (const p of pending) {
    tokenBonus.set(p.ticker, (tokenBonus.get(p.ticker) ?? 0n) + p.tokenDeltaUnits);
  }

  for (const [sym, cat] of catByTicker) {
    const asset = assets.find((a) => a.sym === sym);
    if (!asset) continue;
    const raw = ftByCategory.get(cat) ?? 0n;
    const bonus = tokenBonus.get(sym) ?? 0n;
    // FTs in this project store whole units — if future assets carry
    // decimals, add a per-ticker divisor here.
    asset.bal = Number(raw + bonus);
  }
  for (const asset of assets) {
    if (!RESOLVED.has(asset.sym)) asset.bal = 0;
  }

  // Re-run every derived display the mockup exposes on window. The order
  // matters only weakly (all read from ASSETS), but doing them all ensures
  // the Balances page, swap card, and Katalogos row totals all move in
  // lockstep with the wallet.
  try { (window as unknown as { renderBalances?: () => void }).renderBalances?.(); } catch { /* noop */ }
  try { window.recalc?.(); } catch { /* noop */ }
  try { window.renderKat?.(); } catch { /* noop */ }
  try { paintOdAll(); } catch { /* noop */ }
}

function paintBalanceErr(reason: string): void {
  const bchEl = document.getElementById('chipBch');
  if (bchEl) bchEl.textContent = reason;
}

// ── Live-status banner on Swap / Genesis / Publish ──────────────────────────
// Small always-visible strip that names what's chipnet-live vs still preview
// on the current build. Copy is updated as new paths land — an out-of-date
// "mockup" strip erodes trust more than no strip at all.
function injectPhase1Banner(): void {
  const style = document.createElement('style');
  style.textContent = `
    .wc-p1-banner { font-family: var(--font-mono); font-size: 10px; letter-spacing: .06em; color: #b6f19a; background: rgba(157,240,90,.06); border: 1px solid rgba(157,240,90,.22); border-radius: 8px; padding: 8px 10px; margin: 10px 0; line-height: 1.6; }
    .wc-p1-banner b { color: var(--acid); }
  `;
  document.head.appendChild(style);
  const noteHtml = '<div class="wc-p1-banner">● <b>Live on chipnet</b> · BCH ↔ ASM/ASM2 swaps, Artemision genesis, Katalogos publish, and Share MBA claims all broadcast real transactions. Direct MBA ↔ MBA composite swap and the Oracle template claim path are still preview.</div>';

  // Inject once into the review sheet
  const rev = document.getElementById('reviewSheet');
  if (rev && !rev.querySelector('.wc-p1-banner')) rev.insertAdjacentHTML('afterbegin', noteHtml);
  // …and into the genesis panel
  const gen = document.getElementById('genesisState');
  if (gen && !gen.querySelector('.wc-p1-banner')) gen.insertAdjacentHTML('afterbegin', noteHtml);
  // Publish overlay is dynamically created — patch openPublishOverlay via a MutationObserver
  new MutationObserver((records) => {
    for (const r of records) {
      for (const node of r.addedNodes as unknown as HTMLElement[]) {
        if (node instanceof HTMLElement && node.classList.contains('publish-scrim')) {
          const modal = node.querySelector('.publish-modal');
          if (modal && !modal.querySelector('.wc-p1-banner')) modal.insertAdjacentHTML('afterbegin', noteHtml);
        }
      }
    }
  }).observe(document.body, { childList: true });
}

// ── Boot ────────────────────────────────────────────────────────────────────
wc.on('change', (snap) => {
  renderChip();
  renderScrim();
  if (snap.phase === 'paired') void refreshBalance();
  if (snap.phase === 'idle') {
    currentClient?.close(); currentClient = null;
    // If nothing else is holding balances (no local signer), the WC
    // wallet just disconnected — wipe the UI so we don't show stale
    // numbers from a wallet that isn't attached anymore.
    if (!localSigner) zeroAllBalances();
  }
});

// ── Phase 2a swap-confirm interception — BCH → known MBA claim via WC ──────
// Attaches to #sheetConfirm with capture:true so it runs BEFORE the mockup's
// inline confirmSwap handler. When conditions apply (WC paired + from=BCH +
// to∈KNOWN_MBAS) we take over — stopImmediatePropagation prevents the mock
// from also running; on any other combination the mock still fires.
// dev-signer state — loaded synchronously at boot on localhost when the
// localStorage WIF is set. When present, takes precedence over WC — the
// swap-confirm handler builds + sends the tx directly, no WC round-trip.
let localSigner: Signer | null = null;
type SwapPlan =
  | { ok: true; kind: 'claim'; ticker: string; via: 'wc' | 'dev' }
  | { ok: true; kind: 'redeem'; ticker: string; via: 'dev' }
  | { ok: false };

function templateOf(ticker: string): number {
  const m = KNOWN_MBAS[ticker];
  return (m?.templateId ?? 0x00) & 0xff;
}

/** Templates whose CLAIM path orbital.ts implements. Redeem is separate —
 *  see isRedeemable — because Share MBAs redeem the same way as Artemision
 *  (plain P2PKH burn; the covenant only constrains the claim direction). */
function isClaimable(ticker: string): boolean {
  const t = templateOf(ticker);
  return t === 0x00 || t === 0x01;
}
function isRedeemable(ticker: string): boolean {
  // Both Artemision and Share MBAs self-back each FT UTXO; a plain P2PKH
  // spend that burns the FT amount releases the embedded backing without
  // going through any covenant. Redeem works for both templates.
  const t = templateOf(ticker);
  return t === 0x00 || t === 0x01;
}

function isEligibleRealSwap(): SwapPlan {
  const from = window.from, to = window.to;
  // BCH → MBA: covenant claim() path (WC or local signer)
  if (from === 'BCH' && to && to in KNOWN_MBAS && isClaimable(to)) {
    // WC path only knows the Artemision covenant; Share MBA WC is a follow-up.
    if (localSigner) return { ok: true, kind: 'claim', ticker: to, via: 'dev' };
    if (wc.snapshot.phase === 'paired' && templateOf(to) === 0x00) {
      return { ok: true, kind: 'claim', ticker: to, via: 'wc' };
    }
  }
  // MBA → BCH: plain P2PKH burn + backing-release. Local signer only for now.
  if (to === 'BCH' && from && from in KNOWN_MBAS && isRedeemable(from) && localSigner) {
    return { ok: true, kind: 'redeem', ticker: from, via: 'dev' };
  }
  return { ok: false };
}

async function handleRealSwapConfirm(): Promise<void> {
  const chk = isEligibleRealSwap();
  if (!chk.ok) return;   // mock flow takes over via the default handler
  const amtEl = document.getElementById('amtIn') as HTMLInputElement | null;
  const amtRaw = Number(amtEl?.value);
  const m = KNOWN_MBAS[chk.ticker];

  // Compute amountUnits from the FROM input.
  //   claim (BCH → MBA): amtIn is whole BCH → sats → sats / rate = units
  //   redeem (MBA → BCH): amtIn is whole MBA units → floor to bigint
  let amountUnits: bigint;
  if (chk.kind === 'claim') {
    const grossSats = BigInt(Math.floor(amtRaw * 1e8));
    amountUnits = grossSats / m.rateSats;
    if (amountUnits < 1n) {
      setSheetError(`Amount too small — buy at least ${Number(m.rateSats) / 1e8} BCH worth (1 ${chk.ticker} unit)`);
      return;
    }
    // DeviantOpenMintFT covenant enforces `feeSats >= 546` (dust floor,
    // line 112). feeSats = grossSats × feeBps / 10000. So the smallest
    // valid claim needs grossSats >= ceil(546 × 10000 / feeBps). For ASM
    // (rate 100, fee 100 bps) that means claiming at least ~546 units;
    // for ASM2 (rate 1000, fee 100 bps) about 55 units. Reject client-
    // side with a specific number rather than sending a doomed tx.
    if (m.founderFeeBps > 0n) {
      const feeSats = (grossSats * m.founderFeeBps) / 10000n;
      if (feeSats < 546n) {
        const minGrossSats = (546n * 10000n + m.founderFeeBps - 1n) / m.founderFeeBps; // ceil
        const minUnits = (minGrossSats + m.rateSats - 1n) / m.rateSats;                // ceil
        const minBch = (Number(minGrossSats) / 1e8).toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
        setSheetError(
          `Amount too small — the covenant requires a ${546n} bit fee output; ` +
          `below that the tx is rejected on chain. Minimum claim: ` +
          `${minUnits.toString()} ${chk.ticker} (~${minBch} BCH).`,
        );
        return;
      }
    }
  } else {
    amountUnits = BigInt(Math.floor(amtRaw));
    if (amountUnits < 1n) { setSheetError(`Amount too small — redeem at least 1 ${chk.ticker}`); return; }
  }

  // Show the pending state on the review sheet (the mockup's own UI transition)
  const pendingSheet = document.getElementById('pendingSheet');
  const reviewSheet = document.getElementById('reviewSheet');
  const doneSheet = document.getElementById('doneSheet');
  if (reviewSheet) (reviewSheet as HTMLElement).style.display = 'none';
  if (pendingSheet) (pendingSheet as HTMLElement).style.display = '';
  if (doneSheet) (doneSheet as HTMLElement).style.display = 'none';

  // Reset retry surfaces at the start of every swap (both the live banner in
  // pendingSheet and the resolved-via-N chip on doneSheet).
  paintRetryStatus(null);
  paintDoneRetryBadge(0);
  lastRetryAttempts = 1;

  try {
    if (!currentClient) currentClient = await ElectrumClient.connectAny();
    let result: { txid: string; ticker: string; amountUnits: bigint };
    let doneLabel: string;
    // Split display for the tx-log's From / To columns. Both directions
    // stay in a neutral "X received for Y" register — no "burned"
    // (correct on-chain but the word reads scary/punitive in a UI).
    let logFromAmount: string;
    let logToAmount: string;
    if (chk.kind === 'claim') {
      // Template dispatch — Share MBA's claim has a different tx shape
      // (fee split into two outputs) than Artemision, and its covenant is a
      // different artifact. templateOf(ticker) picks the right variant.
      //
      // Every path here is wrapped with the tier-4 zero-contention retry
      // (Parameters/ASM_Native/ASM-single-asset-throughput.md §2d): on a
      // txn-mempool-conflict from a concurrent claim against the same
      // reservoir tip, the wrapper re-runs the whole build+broadcast (which
      // re-fetches the tip mempool-inclusive) up to 5 attempts. Silent on the
      // happy path; the retry-status banner in pendingSheet shows only when
      // attempt > 1.
      const retryOpts = { onAttempt: setRetryStatus };
      const rr = chk.via === 'dev'
        ? (templateOf(chk.ticker) === 0x01
            ? await claimViaLocalSignerShareWithRetry(localSigner!, { ticker: chk.ticker, amountUnits }, retryOpts)
            : await claimViaLocalSignerWithRetry(localSigner!, { ticker: chk.ticker, amountUnits }, retryOpts))
        : await claimViaWcWithRetry(wc, currentClient, {
            ticker: chk.ticker,
            amountUnits,
            payerCashaddr: wc.snapshot.cashaddr!,
          }, retryOpts);
      const r = rr.result;
      lastRetryAttempts = rr.attempts;
      result = r;
      const bchIn = amtRaw.toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
      logFromAmount = bchIn + ' BCH';
      logToAmount = String(r.amountUnits) + ' ' + r.ticker;
      doneLabel = logToAmount + ' received';
    } else {
      const r = await redeemViaLocalSigner(localSigner!, { ticker: chk.ticker, amountUnits });
      result = { txid: r.txid, ticker: r.ticker, amountUnits: r.amountUnits };
      const bch = (Number(r.releasedSats) / 1e8).toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
      logFromAmount = String(r.amountUnits) + ' ' + r.ticker;
      logToAmount = bch + ' BCH';
      doneLabel = logToAmount + ' received';
    }
    // Flip done sheet; write the real txid + amount
    if (pendingSheet) (pendingSheet as HTMLElement).style.display = 'none';
    if (doneSheet) (doneSheet as HTMLElement).style.display = '';
    // Enrich the done-sheet TX row: short txid text + copy button + link
    // to the chipnet explorer. Trust-cheap upgrade over the raw txid the
    // mockup used to write in as text — first-time users now have a place
    // to verify their broadcast landed on chain.
    const doneTxid = document.getElementById('doneTxid');
    if (doneTxid) renderTxidWithActions(doneTxid, result.txid);
    const doneAmt = document.getElementById('doneAmt');
    if (doneAmt) doneAmt.textContent = doneLabel;
    // Clear the live retry banner and, if the last success used > 1 attempt,
    // show the resolved-via-N chip on the done sheet.
    paintRetryStatus(null);
    paintDoneRetryBadge(lastRetryAttempts);
    // Log the swap for the tx-history column. Capture from/to (symbol +
    // amount) so the renderer can show a full From→To split instead of a
    // single compound string with "burned" in it.
    const fromSym = chk.kind === 'claim' ? (window.from ?? 'BCH') : result.ticker;
    const toSym   = chk.kind === 'claim' ? result.ticker : (window.to ?? 'BCH');
    logTx({
      kind: chk.kind === 'claim' ? 'claim' : 'redeem',
      ticker: result.ticker,
      fromSym, toSym,
      fromAmount: logFromAmount, toAmount: logToAmount,
      amount: doneLabel,   // legacy — still stored for older readers
      txid: result.txid,
    });
    // Refresh balances so the chip + swap card reflect the trade.
    //
    // Chipnet electrum can lag a few seconds on mempool inclusion — a
    // single refresh right after broadcast often returns the PRE-swap
    // UTXO set (the tx is broadcast but not yet indexed at the
    // endpoint), so the BCH chip stays at its old value and the user
    // thinks nothing happened.
    //
    // Two-part fix:
    //   (a) OPTIMISTIC DECREMENT — subtract the sats we know we spent
    //       from the local BCH row and repaint the chip immediately, so
    //       the UI reflects the swap before the electrum server catches
    //       up. On a mismatch the follow-up refresh corrects it.
    //   (b) Multiple follow-up refreshes at 500 ms, 3 s, 8 s — catches
    //       most propagation delays without a busy-poll.
    applyOptimisticSwapDelta(chk.kind, amountUnits, result.ticker, result.txid);
    // Refresh cadence: start after 1.5s (barely-there endpoints often
    // still return stale) and back off. Each refresh runs paintBalance
    // which reapplies the pending overlay — so if the endpoint is stale
    // we keep the post-swap numbers; the pending clears itself the
    // moment the endpoint's BCH reading drops below the pre-swap
    // snapshot (see reapPendingSwaps).
    setTimeout(() => void refreshBalance(), 1500);
    setTimeout(() => void refreshBalance(), 5000);
    setTimeout(() => void refreshBalance(), 12_000);
  } catch (e) {
    setSheetError(e instanceof Error ? e.message : String(e));
    if (pendingSheet) (pendingSheet as HTMLElement).style.display = 'none';
    if (reviewSheet) (reviewSheet as HTMLElement).style.display = '';
    paintRetryStatus(null);
  }
}

/** Live retry state for the current swap — attempt N of MAX. The pending
 *  sheet paints "↻ Reservoir contention — retrying (n/max)…" when non-null
 *  AND attempt > 1. Null clears the banner. */
function setRetryStatus(attempt: number, max: number): void {
  paintRetryStatus(attempt > 1 ? { attempt, max } : null);
}

/** How many attempts the last successful swap took. > 1 = retry saved it. */
let lastRetryAttempts = 1;

function paintRetryStatus(s: { attempt: number; max: number } | null): void {
  const el = document.getElementById('pendingRetry');
  if (!el) return;
  if (s === null) {
    el.style.display = 'none';
    el.textContent = '';
  } else {
    el.style.display = '';
    el.textContent = `↻ Reservoir contention — retrying (${s.attempt}/${s.max})…`;
  }
}

function paintDoneRetryBadge(attempts: number): void {
  const el = document.getElementById('doneRetry');
  if (!el) return;
  if (attempts > 1) {
    el.style.display = '';
    el.textContent = `↻ resolved via ${attempts} attempts (reservoir contention re-broadcast)`;
  } else {
    el.style.display = 'none';
    el.textContent = '';
  }
}

/** Register a just-broadcast swap in `pendingSwaps` so paintBalance
 *  overlays the delta on top of whatever electrum returns, then repaint
 *  every dependent surface synchronously.
 *
 *  Direct mutation of `bchRow.bal` used to work — but the very next
 *  refreshBalance() call would blow it away with the stale electrum
 *  reading (mempool hadn't reached the endpoint yet). Now the delta
 *  lives in `pendingSwaps` and survives every stale refresh until the
 *  endpoint's own BCH reading drops below the pre-swap snapshot
 *  (`reapPendingSwaps` clears it then). The chip therefore stays at the
 *  post-swap value from broadcast → settlement without snapping back.
 *
 *  claim  (BCH → MBA): +bchDelta subtracted off BCH; +amountUnits on MBA
 *  redeem (MBA → BCH): -amountUnits off MBA; +releasedSats onto BCH */
function applyOptimisticSwapDelta(kind: 'claim' | 'redeem', amountUnits: bigint, ticker: string, txid: string): void {
  const assets = window.ASSETS;
  if (!assets) return;
  const m = KNOWN_MBAS[ticker];
  if (!m) return;
  const preSwapBchSats = currentBchSatsFromAssets();
  const minerFee = 3000n;
  const grossSats = amountUnits * m.rateSats;
  const feeSats = (grossSats * m.founderFeeBps) / 10000n;

  if (kind === 'claim') {
    // BCH goes DOWN by gross + fee + miner; MBA goes UP by amountUnits.
    const bchDeltaSats = grossSats + feeSats + minerFee;
    registerPendingSwap({ txid, ticker, bchDeltaSats, tokenDeltaUnits: amountUnits, preSwapBchSats });
  } else {
    // Redeem: MBA burned; BCH goes UP by (gross − fee − miner). We
    // encode this as a NEGATIVE bchDelta so the same overlay math works
    // (paintBalance subtracts, so a negative delta adds).
    const releasedSats = grossSats > feeSats + minerFee ? grossSats - feeSats - minerFee : 0n;
    registerPendingSwap({
      txid,
      ticker,
      bchDeltaSats: -releasedSats,
      tokenDeltaUnits: -amountUnits,
      preSwapBchSats,
    });
  }

  // Immediate repaint. paintBalance is called by refreshBalance; here we
  // just poke the derived surfaces with the current asset state so the
  // chip flips instantly rather than waiting for the first refresh.
  const bchRow = assets.find((a) => a.sym === 'BCH');
  const mbaRow = assets.find((a) => a.sym === ticker);
  if (kind === 'claim') {
    if (bchRow) bchRow.bal = Math.max(0, bchRow.bal - Number(grossSats + feeSats + minerFee) / 1e8);
    if (mbaRow) mbaRow.bal += Number(amountUnits);
  } else {
    const releasedSats = grossSats > feeSats + minerFee ? grossSats - feeSats - minerFee : 0n;
    if (mbaRow) mbaRow.bal = Math.max(0, mbaRow.bal - Number(amountUnits));
    if (bchRow) bchRow.bal += Number(releasedSats) / 1e8;
  }
  const bchEl = document.getElementById('chipBch');
  const bchNow = assets.find((a) => a.sym === 'BCH')?.bal ?? 0;
  if (bchEl) bchEl.textContent = bchNow.toFixed(4) + ' BCH';
  try { (window as unknown as { renderBalances?: () => void }).renderBalances?.(); } catch { /* noop */ }
  try { window.recalc?.(); } catch { /* noop */ }
  try { paintOdAll(); } catch { /* noop */ }
}

/** Classify a raw error message into a short code + one-line headline the
 *  user can scan without reading a wall of text. Broadcast errors from
 *  Bitauth / cashscript often dump the failing tx as a multi-KB hex blob;
 *  the review sheet is not the place for that. Full text stays in the
 *  details section for copy-and-paste debugging. */
function classifySwapError(raw: string): { code: string; headline: string; full: string } {
  const full = raw.trim();
  // Pattern → { code, headline extractor }
  const patterns: Array<{ re: RegExp; code: string; head?: (m: RegExpMatchArray) => string }> = [
    { re: /^insufficient\s+([a-z0-9]+):.*need\s+(\d+).*have\s+(\d+)/i, code: 'E-INSUFFICIENT',
      head: (m) => `Not enough ${m[1].toUpperCase()} — need ${m[2]}, have ${m[3]}` },
    { re: /amount too small|below dust|feeSats\s*>=?/i, code: 'E-DUST',
      head: () => 'Amount below the covenant’s dust floor' },
    { re: /change below dust/i, code: 'E-CHANGE-DUST',
      head: () => 'BCH change would fall below the dust threshold' },
    { re: /covenant may be draining|no state utxo/i, code: 'E-COVENANT-STATE',
      head: () => 'Registry covenant state UTXO missing' },
    { re: /64: bad-txns-inputs-missingorspent|missing inputs/i, code: 'E-STALE-UTXO',
      head: () => 'Chosen UTXOs already spent — endpoint index is stale' },
    { re: /min relay fee|not enough fee/i, code: 'E-FEE-LOW',
      head: () => 'Miner fee too low to relay' },
    { re: /timeout|connect failed|not connected|electrum:/i, code: 'E-NET',
      head: () => 'Electrum endpoint unreachable' },
    { re: /require\s+statement\s+failed|failed\s+at\s+input/i, code: 'E-COVENANT',
      head: (m) => {
        const m2 = full.match(/DeviantOpenMintFT\.cash:(\d+)/i);
        return m2 ? `Covenant require() failed at line ${m2[1]}` : 'Covenant require() failed';
      } },
    { re: /leftover dust padding/i, code: 'E-LEFTOVER-DUST',
      head: () => 'Leftover after redeem would fall below dust' },
    { re: /^redeem too small/i, code: 'E-REDEEM-SMALL',
      head: () => 'Redeem amount too small to cover miner fee' },
  ];
  for (const p of patterns) {
    const m = full.match(p.re);
    if (m) return { code: p.code, headline: p.head ? p.head(m) : m[0], full };
  }
  // Fallback — take the first sentence-ish chunk (up to first period, dash,
  // or 90 chars) as the headline; everything else lives in details.
  const first = full.split(/[.\n]|—/, 1)[0].trim().slice(0, 90);
  return { code: 'E-OTHER', headline: first || 'Swap failed', full };
}

function setSheetError(msg: string): void {
  let el = document.getElementById('wc-sheet-err') as HTMLElement | null;
  if (!el) {
    el = document.createElement('div'); el.id = 'wc-sheet-err';
    const rev = document.getElementById('reviewSheet');
    rev?.insertBefore(el, rev.firstChild);
  }
  el.className = 'wc-sheet-err';
  const { code, headline, full } = classifySwapError(msg);
  const detailsCollapsed = full.length > 120;
  el.innerHTML =
    '<div class="wc-err-row">' +
      '<span class="wc-err-mark">✗</span>' +
      '<span class="wc-err-code" title="Error reference">' + escHtml(code) + '</span>' +
      '<span class="wc-err-head">' + escHtml(headline) + '</span>' +
      '<button class="wc-err-btn" data-err-copy="1" title="Copy full error text">⧉</button>' +
      (detailsCollapsed
        ? '<button class="wc-err-btn" data-err-toggle="1" title="Show / hide full text">⌄</button>'
        : '') +
    '</div>' +
    (detailsCollapsed
      ? '<div class="wc-err-full" hidden>' + escHtml(full) + '</div>'
      : (full !== headline ? '<div class="wc-err-full">' + escHtml(full) + '</div>' : ''));
  el.querySelector<HTMLButtonElement>('[data-err-copy]')?.addEventListener('click', async (ev) => {
    ev.stopPropagation();
    try {
      await navigator.clipboard.writeText('[' + code + '] ' + full);
      const btn = ev.currentTarget as HTMLButtonElement;
      const orig = btn.textContent;
      btn.textContent = '✓';
      setTimeout(() => { btn.textContent = orig; }, 1000);
    } catch { /* clipboard blocked — silent */ }
  });
  el.querySelector<HTMLButtonElement>('[data-err-toggle]')?.addEventListener('click', (ev) => {
    ev.stopPropagation();
    const details = el!.querySelector<HTMLElement>('.wc-err-full');
    if (details) details.hidden = !details.hidden;
  });
}

function wireSwapConfirmInterceptor(): void {
  const btn = document.getElementById('sheetConfirm');
  if (!btn) return;
  btn.addEventListener('click', (ev) => {
    const from = window.from, to = window.to;
    const chk = isEligibleRealSwap();
    if (chk.ok) {
      ev.stopImmediatePropagation();
      ev.preventDefault();
      void handleRealSwapConfirm();
      return;
    }
    // Everything else: kill the mockup's fake-broadcast path entirely.
    // Previously we let it fall through for pairs the real handler didn't
    // yet recognise ("Share via WC, Oracle, …") — but the mockup mutates
    // the local balances as if a swap had happened, which caused BCH to
    // appear to move even when nothing was broadcast on chain. The user
    // reported "BCH not deducted after swap" — that was the mockup running
    // while no wallet was attached. Now we ALWAYS block propagation and
    // surface a specific reason on the review sheet:
    ev.stopImmediatePropagation();
    ev.preventDefault();
    const pairTouchesDea = from === 'DEA' || to === 'DEA';
    if (pairTouchesDea) {
      setSheetError(
        `${from} → ${to} swap isn't wired yet. DEA has no mint/burn covenant — ` +
        `it is the platform fee-token; you receive DEA when a covenant pays you ` +
        `and spend it on Publish fees. Pick a BCH ↔ MBA pair to swap.`,
      );
      return;
    }
    const hasSigner = !!localSigner;
    const hasWc = wc.snapshot.phase === 'paired';
    if (!hasSigner && !hasWc) {
      setSheetError(
        `Attach a wallet before swapping. Use the wallet chip in the top-right — ` +
        `Create a wallet, Import a WIF, or pair via WizardConnect. Nothing is ` +
        `broadcast until a wallet signs.`,
      );
      return;
    }
    // Wallet is present but the pair isn't covered by the real handler
    // (e.g. MBA → MBA composite, or an Oracle-template claim). Say so
    // rather than pretending to broadcast.
    if (from && to) {
      const fromInMba = from in KNOWN_MBAS;
      const toInMba = to in KNOWN_MBAS;
      if (fromInMba && toInMba) {
        setSheetError(
          `${from} → ${to}: direct MBA → MBA swap isn't wired yet. For now, ` +
          `redeem ${from} → BCH first, then claim BCH → ${to}.`,
        );
        return;
      }
    }
    setSheetError(
      `${from} → ${to}: this pair isn't wired for on-chain swap yet. ` +
      `Only BCH ↔ (ASM, ASM2, Share MBAs) is live today.`,
    );
  }, { capture: true });
}

async function bootLocalSigner(): Promise<void> {
  localSigner = loadActiveSigner();
  if (!localSigner) return;
  console.log('[signer]', localSigner.mode, '· cashaddr', localSigner.cashaddr);
  renderChip();
  void refreshBalance();
  // Pull cross-device tx history for the resumed signer. Deferred so the
  // balance fetch grabs its RPC slot first; hydrate is idempotent (txids
  // are deduped) so a duplicate call from a later signer-change is a
  // no-op.
  const s = localSigner;
  setTimeout(() => void hydrateTxLogFromChain(s), 500);
}

function boot(): void {
  // Restore any pending-swap overlays from the previous session so a
  // page reload mid-flight doesn't lose them while electrum is still
  // catching up to the broadcast.
  loadPendingSwapsFromStorage();
  attachWellKnownIcons();
  hydrateLocalMbas();
  filterAssetsToSwappable();
  renderChip();
  paintWalletMenu();
  injectPhase1Banner();
  wireSwapConfirmInterceptor();
  wireArtemisionBroadcast();
  injectTemplatePicker();
  wireIconAutoUpload();
  wirePublishInterceptor();
  wireOdLayout();
  installAmountPlaceholder();
  installBalancesUsdPatch();
  // Balances page + swap card still show the mockup's hard-coded numbers
  // until a wallet attaches. Zero them out on boot so the user sees "you
  // hold nothing" honestly — the moment a wallet attaches, paintBalance
  // fills in the real chipnet numbers. If a signer is already loaded from
  // localStorage, bootLocalSigner immediately fetches + paints; the wipe
  // is instantaneous and invisible.
  zeroAllBalances();
  void bootLocalSigner();
  void refreshBchUsd().then(() => updateAmountPlaceholder());
  void refreshRegistry();
  paintMyAssets();
  paintWelcomeOverlay();
  // DEA Yield tab — live vault pool + user pro-rata. Reuses the same
  // currentClient the balance chip uses (widened CHIPNET_ENDPOINTS pool,
  // built-in connect timeout, connect-any semantics); if not yet connected,
  // opens one lazily on first refresh.
  wireDeaYield(
    async () => {
      if (!currentClient) {
        try { currentClient = await ElectrumClient.connectAny(); }
        catch { return null; }
      }
      return currentClient;
    },
    () => localSigner?.cashaddr ?? wc.snapshot.cashaddr ?? null,
  );
}

// ── Katalogos registry read — surface who is registered on-chain ───────────
// Deliberately best-effort + view-only for v1. See registry.ts for why the
// swap picker cannot include unknown-metadata entries yet (v1 stores the
// category id but not the rate/fee/founder needed to build a claim tx).
// What we can do today: show a "registered on chipnet" chip in the Katalogos
// tab so users see the on-chain list grow past our static seed.

let registryEntries: RegisteredEntry[] = [];

async function refreshRegistry(): Promise<void> {
  try {
    if (!currentClient) currentClient = await ElectrumClient.connectAny();
    const entries = await readKatalogos(currentClient);
    registryEntries = entries;
    console.log('[katalogos]', entries.length, 'entries on chipnet:', entries.map((e) => e.categoryId.slice(0, 8)));
    (window as unknown as { potidaeaRegistry: RegisteredEntry[] }).potidaeaRegistry = entries;
    paintRegistryBanner();

    // Cross-user metadata read: for each entry, try the DVMB OP_RETURN
    // sidecar. Successes fold into KNOWN_MBAS + ASSETS so wallets that
    // have NEVER seen this asset before can now swap it. Fetches are
    // parallel with generous timeouts — a per-entry failure is logged
    // and skipped.
    let mergedCount = 0;
    const results = await Promise.allSettled(entries.map((e) => fetchEntryParams(currentClient!, e).then((p) => ({ e, p }))));
    for (const r of results) {
      if (r.status !== 'fulfilled' || !r.value.p) continue;
      try {
        const km = await discoveredParamsToKnownMba(r.value.e, r.value.p);
        if (!km) continue;
        // Skip K:xxx placeholder tickers — those are Katalogos entries whose
        // DVMB sidecar didn't carry a ticker chunk. They're valid on chain
        // and stay in the Katalogos view (categoryId is first-class there),
        // but "K:c49855" as a swap-picker row is noise the user can't
        // meaningfully act on. The registry banner still counts them.
        if (km.ticker.startsWith('K:')) continue;
        // Prefer static seed / local cache over discovered — those already
        // ran the full genesis + local metadata contract.
        if (!KNOWN_MBAS[km.ticker] || KNOWN_MBAS[km.ticker].categoryId === km.categoryId) {
          mergeKnownMbas({ [km.ticker]: km });
          const assets = window.ASSETS;
          if (assets && !assets.find((a) => a.sym === km.ticker)) {
            const rawName = r.value.p.name ?? km.ticker;
            // Only append the provenance suffix once — earlier fallback
            // sometimes already embedded "from Katalogos" into the ticker
            // shorthand.
            const displayName = /from Katalogos/i.test(rawName) ? rawName : rawName + ' · from Katalogos';
            assets.push({
              sym: km.ticker,
              name: displayName,
              glyph: km.ticker.slice(0, 2),
              rate: Number(km.rateSats),
              cls: 'MBA',
              bal: 0,
              pub: true,
            });
          }
          // Icon sidecar — fire-and-forget verify+render. On success the
          // ASSETS row's iconUrl gets set and the next recalc/renderKat
          // paints the <img>. On any failure we silently keep the glyph
          // fallback (no leak, no scary UI — a bad icon means "this
          // publisher's Sia path is broken", not "the asset is bad").
          const icon = r.value.p;
          if (icon.iconSha256Hex && icon.iconPath) {
            void fetchAndVerifyIcon(icon.iconPath, icon.iconSha256Hex).then((dataUrl) => {
              if (!dataUrl) return;
              const row = window.ASSETS?.find((a) => a.sym === km.ticker) as
                | { iconUrl?: string } | undefined;
              if (row) row.iconUrl = dataUrl;
              try { window.recalc?.(); } catch { /* noop */ }
              try { window.renderKat?.(); } catch { /* noop */ }
              try { (window as unknown as { renderBalances?: () => void }).renderBalances?.(); } catch { /* noop */ }
            });
          }
          mergedCount++;
        }
      } catch (err) {
        console.warn('[katalogos] discover fail for', r.value.e.categoryId.slice(0, 12), err instanceof Error ? err.message : err);
      }
    }
    if (mergedCount) {
      console.log('[katalogos] discovered params for', mergedCount, 'entries via DVMB sidecars');
      try { window.recalc?.(); } catch { /* noop */ }
      try { window.renderKat?.(); } catch { /* noop */ }
    }
    // Registry read done → always repaint the assets browser (both success
    // and no-new-merges paths reach here) so the "Listed" filter reflects
    // the freshest count. Also repaint the My-assets dashboard so its
    // "Listed on Katalogos" pill toggles as soon as a publish confirms.
    try { paintOdAll(); } catch { /* noop */ }
    try { paintMyAssets(); } catch { /* noop */ }
  } catch (e) {
    console.warn('[katalogos] refresh failed:', e instanceof Error ? e.message : e);
  }
}

// ── My assets · Balances dashboard block ───────────────────────────────
// Wallet-scoped list of every asset genesised through Artemision from
// this wallet. Sources from `loadLocalKnownMbas()` (the localStorage
// cache filled by saveLocalAsset during the wizard broadcast). Katalogos
// is cross-wallet and public; this block is your creator ledger.
//
// Idempotent: safe to call after boot, after registry refresh, after a
// wizard broadcast, after a publish, or on view switch. Each row's
// "Listed on Katalogos" pill reflects `registryEntries` at call time.
function paintMyAssets(): void {
  const listEl = document.getElementById('myAssetsList');
  if (!listEl) return;
  const local = loadLocalKnownMbas();
  const localList = Object.values(local);
  const subEl = document.getElementById('maSub');
  if (subEl) {
    subEl.textContent = localList.length === 0
      ? 'Assets you have minted through Artemision from this wallet'
      : `${localList.length} asset${localList.length === 1 ? '' : 's'} minted from this wallet · ` +
        `${localList.filter((m) => (registryEntries ?? []).some((e) => e.categoryId === m.categoryId)).length} published`;
  }

  // Wire the "+ New asset" CTA + the empty-state Artemision link to the
  // nav handler. Delegated so re-renders don't stack listeners.
  const block = document.getElementById('myAssetsBlock');
  if (block && !(block as HTMLElement & { __gotoWired?: boolean }).__gotoWired) {
    block.addEventListener('click', (ev) => {
      const t = ev.target as HTMLElement | null;
      const goto = t?.closest('[data-goto]') as HTMLElement | null;
      if (goto) {
        ev.preventDefault();
        const nav = document.querySelector(`.navitem[data-view="${goto.getAttribute('data-goto')}"]`);
        (nav as HTMLElement | null)?.click();
      }
    });
    (block as HTMLElement & { __gotoWired?: boolean }).__gotoWired = true;
  }

  if (localList.length === 0) {
    listEl.innerHTML =
      '<div class="ma-empty">' +
        'You have not minted any assets from this wallet yet.<br>' +
        'Head to <a data-goto="artemision">Artemision · Create</a> to spin up your first backed asset — ' +
        'it lands in your list immediately, then you can publish to the shared Katalogos in a click.' +
      '</div>';
    return;
  }

  // Sort: newest first if the local cache preserves order (it appends);
  // fall back to categoryId lex order for stability.
  const publishedCats = new Set((registryEntries ?? []).map((e) => e.categoryId));

  const rows = localList.map((m) => {
    const iconUrl = (m as unknown as { iconUrl?: string }).iconUrl;
    const glyph = (m as unknown as { glyph?: string }).glyph ?? m.ticker.slice(0, 2);
    const name = (m as unknown as { name?: string }).name ?? '';
    const listed = publishedCats.has(m.categoryId);
    const tpl = m.templateId;
    const rateBch = Number(m.rateSats) / 1e8;
    // Format the rate: for tiny rates (ASM at 100 sats/unit → 0.000001 BCH),
    // strip the trailing 0s so it reads as "0.000001 BCH/unit".
    const rateStr = rateBch >= 0.01 ? rateBch.toFixed(6) : rateBch.toFixed(8);
    const rateTrim = rateStr.replace(/0+$/, '').replace(/\.$/, '');
    const feePct = (Number(m.founderFeeBps) / 100).toFixed(m.founderFeeBps % 100n === 0n ? 0 : 2);

    const icoInner = iconUrl
      ? `<img src="${escAttr(iconUrl)}" alt="">`
      : escHtml(glyph);
    const tplBadge = tpl === 0x01
      ? '<span class="ma-pill share">Share</span>'
      : tpl === 0x02
        ? '<span class="ma-pill oracle">Oracle</span>'
        : '<span class="ma-pill" style="color:var(--ink-soft); border-color:var(--border-strong)">Static</span>';
    const statusBadge = listed
      ? '<span class="ma-pill listed">✓ Listed on Katalogos</span>'
      : '<span class="ma-pill local">Local only</span>';
    const catShort = m.categoryId.slice(0, 8) + '…' + m.categoryId.slice(-6);
    const publishBtn = listed
      ? ''
      : `<button class="ma-btn primary" data-publish="${escAttr(m.ticker)}" title="Publish to the shared Katalogos (~$1 in DEA)">Publish →</button>`;
    const swapBtn = `<button class="ma-btn" data-swap="${escAttr(m.ticker)}" title="Open the swap card with ${escAttr(m.ticker)} preselected">Swap →</button>`;
    const genesisTxid = (m as unknown as { genesisTxid?: string }).genesisTxid;
    const explorerBtn = genesisTxid
      ? `<a class="ma-btn" href="https://chipnet.imaginary.cash/tx/${encodeURIComponent(genesisTxid)}" target="_blank" rel="noopener noreferrer" title="View genesis tx on chipnet explorer">↗</a>`
      : '';

    return (
      `<div class="ma-row" data-ticker="${escAttr(m.ticker)}">` +
        `<div class="ma-ico">${icoInner}</div>` +
        `<div class="ma-mid">` +
          `<div class="ma-line1">` +
            `<span class="ma-sym">${escHtml(m.ticker)}</span>` +
            (name ? `<span class="ma-name">· ${escHtml(name)}</span>` : '') +
            tplBadge +
            statusBadge +
          `</div>` +
          `<div class="ma-line2">` +
            `<span><span class="k">rate</span> ${escHtml(rateTrim)} BCH/unit</span>` +
            `<span><span class="k">fee</span> ${escHtml(feePct)}%</span>` +
            `<button class="ma-cat" data-copy="${escAttr(m.categoryId)}" title="Copy full category id">${escHtml(catShort)}</button>` +
          `</div>` +
        `</div>` +
        `<div class="ma-actions">` +
          swapBtn +
          publishBtn +
          explorerBtn +
        `</div>` +
      `</div>`
    );
  }).join('');

  listEl.innerHTML = `<div class="ma-list">${rows}</div>`;

  // Wire copy-category-id buttons.
  listEl.querySelectorAll<HTMLButtonElement>('[data-copy]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const cat = btn.getAttribute('data-copy') ?? '';
      try {
        await navigator.clipboard.writeText(cat);
        const orig = btn.textContent ?? '';
        btn.textContent = '✓ copied';
        btn.classList.add('copied');
        setTimeout(() => { btn.textContent = orig; btn.classList.remove('copied'); }, 1200);
      } catch { /* clipboard blocked in preview iframes — silent */ }
    });
  });

  // Wire publish buttons to the existing overlay (openPublishOverlay is
  // installed on window by main.ts's wirePublishInterceptor).
  listEl.querySelectorAll<HTMLButtonElement>('[data-publish]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const sym = btn.getAttribute('data-publish') ?? '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const open = (window as any).openPublishOverlay;
      if (typeof open === 'function') open(sym);
    });
  });

  // Wire "Swap →" buttons — set from=BCH / to=<ticker> then jump to the
  // swap view. Uses the mockup's global `from` / `to` + `recalc` (both
  // set on window via the mockup's non-strict inline script).
  listEl.querySelectorAll<HTMLButtonElement>('[data-swap]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const sym = btn.getAttribute('data-swap') ?? '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      w.from = 'BCH';
      w.to = sym;
      try { w.recalc?.(); } catch { /* noop */ }
      const nav = document.querySelector('.navitem[data-view="swap"]');
      (nav as HTMLElement | null)?.click();
    });
  });
}

// Expose so other paint hooks (post-broadcast, post-publish) can trigger a
// refresh without importing across files.
(window as unknown as { paintMyAssets?: () => void }).paintMyAssets = paintMyAssets;

function paintRegistryBanner(): void {
  // Attach a small pill on the Katalogos tab's Stoa panel that says how many
  // entries live on-chain vs how many we can transact with. Non-intrusive:
  // if the target element doesn't exist (mockup swap), we no-op.
  const target = document.getElementById('kt-stoa') || document.querySelector('.view#v-katalogos');
  if (!target) return;
  let banner = document.getElementById('wc-reg-banner');
  if (!banner) {
    banner = document.createElement('div');
    banner.id = 'wc-reg-banner';
    banner.style.cssText = 'font-family:var(--font-mono);font-size:11px;letter-spacing:.04em;color:var(--acid);background:rgba(157,240,90,.06);border:1px solid rgba(157,240,90,.22);border-radius:8px;padding:8px 12px;margin:10px 0;line-height:1.55;';
    target.insertBefore(banner, target.firstChild);
  }
  const knownSyms = Object.keys((window as unknown as { potidaeaKnown?: unknown }).potidaeaKnown ?? {}); // hook for future use
  void knownSyms;
  const n = registryEntries.length;
  if (n === 0) {
    banner.textContent = '⌁ Katalogos on chipnet · empty (no on-chain listings yet)';
  } else {
    const shorts = registryEntries.slice(0, 3).map((e) => e.categoryId.slice(0, 8) + '…').join(' · ');
    const more = n > 3 ? ` · +${n - 3} more` : '';
    banner.innerHTML = '⌁ Katalogos on chipnet · <b>' + n + ' asset' + (n === 1 ? '' : 's') + '</b> registered · <span style="color:var(--muted)">' + shorts + more + '</span><br>' +
      '<span style="color:var(--muted);font-size:10px">v1 registry stores the category id only; rate/fee/founder come from a metadata seed. New listings are visible here but not yet swappable in Potidaea until a v2 covenant carries the parameters (ADR-0055 Amendment 2026-09-02).</span>';
  }
}

// Filter the mockup's ASSETS array down to what actually swaps on chipnet:
//   • BCH always
//   • Every ticker in KNOWN_MBAS (seed + local cache + DVMB-discovered)
// The mockup's hardcoded demo tickers (GMX, CHE, ATD, …) have no covenant
// wired, so keeping them in the picker was misleading — user picks GMX and
// the "swap" is inert. Now they're removed from the picker (and by
// extension from every derived surface: balances page, Katalogos rows,
// review sheet). Real GMX / CHE covenants can be added later by seeding
// KNOWN_MBAS with their categoryId + params.
function filterAssetsToSwappable(): void {
  const assets = window.ASSETS;
  if (!assets) return;
  // DEA is a base-currency the chip strip pins even though it isn't in
  // KNOWN_MBAS (Potidaea's swap covenants are BCH-family only). Keeping
  // it means the swap card can display DEA/TICKER pairs; the actual
  // execute path still refuses via isEligibleRealSwap and falls through
  // to the mock flow — honest.
  // Exclude K:<hex> placeholders — these are Katalogos-discovered entries
  // whose DVMB sidecar didn't carry a proper ticker. They ARE on-chain
  // and technically swappable, but "K:c49855" as a picker row is noise:
  // the user can't tell what they're picking. They remain visible in the
  // Katalogos view where the categoryId is the first-class citizen; the
  // swap picker only shows real-name tickers.
  const swappableKnown = Object.keys(KNOWN_MBAS).filter((sym) => !sym.startsWith('K:'));
  const KEEP = new Set<string>(['BCH', 'DEA', ...swappableKnown]);
  const removed = assets.filter((a) => !KEEP.has(a.sym)).map((a) => a.sym);
  window.ASSETS = assets.filter((a) => KEEP.has(a.sym));
  if (removed.length) console.log('[assets] filtered non-swappable:', removed);
  // Rewire from/to so the mockup's picker never lands on a removed sym.
  // Default to BCH → first non-BCH swappable, matching the "buy an asset
  // with BCH" mental model most users start with.
  const nonBch = window.ASSETS.find((a) => a.sym !== 'BCH');
  window.from = 'BCH';
  window.to = nonBch?.sym ?? 'BCH';
  try { window.recalc?.(); } catch { /* noop */ }
}

// Merge locally-genesised MBAs into KNOWN_MBAS + ensure they surface as
// swap-picker rows on the mockup's ASSETS array. This runs at boot so a
// user who created an asset in a prior session sees it immediately.
function hydrateLocalMbas(): void {
  const local = loadLocalKnownMbas();
  const localCount = Object.keys(local).length;
  if (localCount === 0) return;
  mergeKnownMbas(local);
  const assets = window.ASSETS;
  if (!assets) return;
  for (const [ticker, m] of Object.entries(local)) {
    if (assets.find((a) => a.sym === ticker)) continue;
    assets.push({
      sym: ticker,
      name: m.name ?? ticker + ' · your asset',
      glyph: m.glyph ?? ticker.slice(0, 2),
      rate: Number(m.rateSats),
      cls: 'MBA',
      bal: 0,
      pub: false,
      iconUrl: m.iconUrl,
    });
  }
  console.log('[hydrate] merged', localCount, 'local MBAs into KNOWN_MBAS');
}

// Wizard "Broadcast genesis" button — replaces the mockup's illustrative
// pseudo-txid handler with a real ArtemisionMintFT deploy on chipnet.
// Requires an attached local signer (WC-side genesis is a follow-up — same
// tx shape, needs the inputPaths triplet + a wallet that supports it).
function wireArtemisionBroadcast(): void {
  const btn = document.getElementById('wizBroadcast');
  if (!btn) return;
  btn.addEventListener('click', (ev) => {
    if (!localSigner) return;         // no signer → mock's illustrative flow takes over
    ev.stopImmediatePropagation();
    ev.preventDefault();
    void runArtemisionGenesis();
  }, { capture: true });
}

// Template picker — injected at the top of wizard step 3. Two options today:
//   • Static (Artemision)  → genesisArtemisionMBA (existing default)
//   • Shares + dividends   → genesisDeviantShareMBA (fee split at consensus)
// Selecting Shares reveals a small panel with the three extra fields the
// Share covenant needs: companySplitBps + company address + distribution
// address. Both addresses default to the signer's own bchtest: address so a
// solo dev can smoke the flow without staging separate keys.
function injectTemplatePicker(): void {
  const step3 = document.querySelector('.wiz-body[data-body="3"]');
  if (!step3 || document.getElementById('wc-tpl-picker')) return;

  // Segmented control at the top
  const picker = document.createElement('div');
  picker.id = 'wc-tpl-picker';
  picker.className = 'fld';
  picker.innerHTML =
    '<label>Template <span style="opacity:.6; text-transform:none; letter-spacing:0">(family of covenant · picked once at genesis)</span></label>' +
    '<div class="wc-tpl-seg">' +
      '<button type="button" class="wc-tpl-opt on" data-tpl="artemision">' +
        '<b>Static</b><span>Artemision · fixed backing rate + founder fee</span>' +
      '</button>' +
      '<button type="button" class="wc-tpl-opt" data-tpl="share">' +
        '<b>Shares + dividends</b><span>DeviantShareMBA · fee splits into company + distribution streams</span>' +
      '</button>' +
    '</div>';
  step3.insertBefore(picker, step3.firstChild?.nextSibling ?? null);

  // Share-only panel (hidden by default). Placed just after the fee row.
  const feeRow = document.getElementById('asFee')?.closest('.fld-row');
  if (feeRow) {
    const sharePanel = document.createElement('div');
    sharePanel.id = 'wc-share-panel';
    sharePanel.style.cssText = 'display:none; margin-top:14px; padding:14px; border:1px solid rgba(214,255,61,.22); background:rgba(214,255,61,.04); border-radius:10px;';
    sharePanel.innerHTML =
      '<div style="font-family:var(--font-mono); font-size:10px; letter-spacing:.16em; text-transform:uppercase; color:var(--acid); margin-bottom:10px">Shares + dividends</div>' +
      '<div class="fld-row">' +
        '<div class="fld"><label>Company share of fee (bps · max 10000)</label><input id="asCompanySplit" value="5000" inputmode="numeric"></div>' +
        '<div class="fld"><label style="opacity:.7">Shareholder stream</label><div style="font-family:var(--font-mono); font-size:12px; color:var(--muted); padding:10px 0">= fee − company (auto)</div></div>' +
      '</div>' +
      '<div class="fld"><label>Company address <span style="opacity:.6; text-transform:none; letter-spacing:0">(bchtest: — defaults to your wallet)</span></label><input id="asCompanyAddr" placeholder="bchtest:qz…" autocomplete="off" spellcheck="false"></div>' +
      '<div class="fld"><label>Distribution address <span style="opacity:.6; text-transform:none; letter-spacing:0">(bchtest: — where shareholders\' stream lands · defaults to your wallet)</span></label><input id="asDistribAddr" placeholder="bchtest:qz…" autocomplete="off" spellcheck="false"></div>';
    feeRow.parentNode?.insertBefore(sharePanel, feeRow.nextSibling);
  }

  // Small local stylesheet for the segmented control
  const style = document.createElement('style');
  style.textContent =
    '.wc-tpl-seg { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 6px; }' +
    '.wc-tpl-opt { background: var(--surface-2); border: 1px solid var(--border-strong); color: var(--ink-bright); padding: 12px 14px; border-radius: 10px; cursor: pointer; text-align: left; font-family: var(--font-display); transition: border-color .12s, background .12s; }' +
    '.wc-tpl-opt b { display: block; font-size: 14px; font-weight: 500; }' +
    '.wc-tpl-opt span { display: block; font-family: var(--font-mono); font-size: 10px; color: var(--muted); margin-top: 4px; line-height: 1.5; }' +
    '.wc-tpl-opt:hover { border-color: rgba(214, 255, 61, .35); }' +
    '.wc-tpl-opt.on { border-color: var(--acid); background: rgba(214, 255, 61, .06); }' +
    '.wc-tpl-opt.on b { color: var(--acid); }';
  document.head.appendChild(style);

  // Click handling
  picker.querySelectorAll('.wc-tpl-opt').forEach((btn) => {
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      picker.querySelectorAll('.wc-tpl-opt').forEach((b) => b.classList.remove('on'));
      (btn as HTMLElement).classList.add('on');
      const tpl = btn.getAttribute('data-tpl') || 'artemision';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).wizTemplate = tpl;
      const panel = document.getElementById('wc-share-panel');
      if (panel) panel.style.display = (tpl === 'share') ? '' : 'none';
    });
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).wizTemplate = 'artemision';
}

function readTemplate(): 'artemision' | 'share' {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const t = (window as any).wizTemplate;
  return t === 'share' ? 'share' : 'artemision';
}

async function runArtemisionGenesis(): Promise<void> {
  if (!localSigner) return;
  const ticker = (document.getElementById('asSym') as HTMLInputElement | null)?.value?.trim() || 'NEW';
  const name   = (document.getElementById('asName') as HTMLInputElement | null)?.value?.trim() || undefined;
  const glyph  = (document.getElementById('asGlyph') as HTMLInputElement | null)?.value?.trim() || undefined;
  const rateStr = (document.getElementById('asRate') as HTMLInputElement | null)?.value?.trim() || '100';
  const feePct = parseFloat((document.getElementById('asFee') as HTMLInputElement | null)?.value ?? '0');
  const rateSats = BigInt(Math.max(1, Math.floor(Number(rateStr))));
  const feeBps = BigInt(Math.max(0, Math.min(500, Math.round(feePct * 100))));
  const tpl = readTemplate();

  const state = document.getElementById('genesisState');
  if (state) state.innerHTML =
    '<div class="review-row"><span class="rk">Broadcasting</span><span class="rv acid">' + escHtml(ticker) + ' · ' + (tpl === 'share' ? 'Shares' : 'Static') + ' · rate ' + String(rateSats) + ' sats/unit · fee ' + String(feeBps) + ' bps</span></div>' +
    '<div style="font-family:var(--font-mono); font-size:11px; color: var(--muted); margin-top: 12px">Waiting for chipnet confirmation…</div>';

  try {
    let res: GenesisResult | ShareGenesisResult;
    if (tpl === 'share') {
      const splitStr = (document.getElementById('asCompanySplit') as HTMLInputElement | null)?.value?.trim() || '5000';
      const companySplitBps = BigInt(Math.max(0, Math.min(10000, Math.floor(Number(splitStr)))));
      const companyAddrRaw = (document.getElementById('asCompanyAddr') as HTMLInputElement | null)?.value?.trim() || '';
      const distribAddrRaw = (document.getElementById('asDistribAddr') as HTMLInputElement | null)?.value?.trim() || '';
      const companyLBHex = companyAddrRaw ? cashaddrToP2PKHLBHex(companyAddrRaw) : pkhToP2PKHLBHex(localSigner.publicKeyHash);
      const distribLBHex = distribAddrRaw ? cashaddrToP2PKHLBHex(distribAddrRaw) : pkhToP2PKHLBHex(localSigner.publicKeyHash);
      if (!companyLBHex) throw new Error('Invalid company address — expected bchtest: cashaddr');
      if (!distribLBHex) throw new Error('Invalid distribution address — expected bchtest: cashaddr');
      const shareRes: ShareGenesisResult = await genesisDeviantShareMBA(localSigner, {
        ticker, rateSats, feeBps, companySplitBps,
        companyLockingBytecodeHex: companyLBHex,
        distributionLockingBytecodeHex: distribLBHex,
        name, glyph,
      });
      res = shareRes;
    } else {
      res = await genesisArtemisionMBA(localSigner, { ticker, rateSats, founderFeeBps: feeBps, name, glyph });
    }
    logTx({ kind: 'genesis', ticker, amount: (tpl === 'share' ? 'Shares' : 'Static'), txid: res.txid });
    // Now visible in KNOWN_MBAS (via saveLocalAsset inside genesis) — but we
    // also want to re-hydrate ASSETS in place so the picker refreshes.
    hydrateLocalMbas();

    // Icon path: prefer the URL the file-change auto-upload already
    // produced (see wireIconAutoUpload). If that didn't fire — user
    // pasted a WIF, opened a wizard from cache, or auto-upload failed —
    // fall back to uploading from the wizard's data-URL preview now.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const row = window.ASSETS?.find((a) => a.sym === ticker) as
      | { iconSha256?: string; iconPath?: string; iconUrl?: string } | undefined;
    if (typeof w.wizIconUploadedUrl === 'string' && typeof w.wizIconUploadedSha === 'string') {
      if (row) { row.iconSha256 = w.wizIconUploadedSha; row.iconPath = w.wizIconUploadedUrl; }
    } else if (typeof w.wizIconDataUrl === 'string' && w.wizIconDataUrl.startsWith('data:')) {
      try {
        const { bytes, contentType } = dataUrlToBytes(w.wizIconDataUrl);
        const upload = await uploadIcon(bytes, contentType);
        if (row) { row.iconSha256 = upload.sha256Hex; row.iconPath = upload.url; }
        console.log('[icon] uploaded at genesis-time', upload.bytes, 'bytes →', upload.url);
      } catch (e) {
        const msg = e instanceof IconUploadError ? '[' + e.code + '] ' + e.message : String(e);
        console.warn('[icon] upload skipped:', msg);
      }
    }
    try { window.recalc?.(); } catch { /* noop */ }
    try { window.renderKat?.(); } catch { /* noop */ }
    // New asset shows up in the Balances → My assets dashboard on the very
    // next repaint. Local-only pill; flips to "Listed on Katalogos" after
    // the user hits Publish.
    try { paintMyAssets(); } catch { /* noop */ }
    // Wipe the wizard draft — the fields are baked into the genesis tx now,
    // and re-opening the wizard should start clean.
    try { sessionStorage.removeItem('deviant.potidaea.wizard-draft.v1'); } catch { /* noop */ }
    if (state) {
      state.innerHTML =
        '<div class="done-mark"><div class="dm">✓</div>' +
          '<h4>' + escHtml(ticker) + ' minted on chipnet</h4>' +
          '<p>Genesis broadcast — <b>added to your swap picker</b> automatically. Ready to trade.</p>' +
          '<div class="txid" id="genesisReceiptTx"></div>' +
          '<div class="txid" style="font-size:10px; opacity:.7">category ' + escHtml(res.categoryId) + '</div>' +
          '<div style="margin-top:22px; padding:16px; border-radius:12px; border:1px solid rgba(214,255,61,.3); background:rgba(214,255,61,.05); text-align:left">' +
            '<div style="font-family:var(--font-mono); font-size:10px; letter-spacing:.16em; text-transform:uppercase; color:var(--acid); margin-bottom:8px">Optional · publish to the shared Katalogos</div>' +
            '<p style="margin:0 0 12px; font-size:13px; line-height:1.7; color:var(--ink-soft)">Inscribes ' + escHtml(ticker) + ' into the on-chain registry every wallet reads. Costs <b>$1</b> in DEA (oracle-priced at broadcast).</p>' +
            '<button class="wiz-btn primary" style="width:100%" onclick="openPublishOverlay(\'' + escAttr(ticker) + '\')">Publish for $1 in DEA →</button>' +
          '</div>' +
        '</div>';
      const gTxEl = document.getElementById('genesisReceiptTx');
      if (gTxEl) renderTxidWithActions(gTxEl, res.txid);
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (state) state.innerHTML =
      '<div style="font-family:var(--font-mono); font-size:12px; color: #ff7a8a; background: rgba(255,122,138,.08); border: 1px solid rgba(255,122,138,.28); border-radius: 8px; padding: 12px">' +
      '✗ Genesis failed<br><span style="font-size:11px; opacity:.8">' + escHtml(msg) + '</span></div>' +
      '<button class="wiz-btn primary" style="width:100%; margin-top:16px" onclick="location.reload()">Reload</button>';
  }
}

// Publish button — replaces the mockup's illustrative openPublishOverlay
// with a real KatalogosRegistryV1.register() broadcast when a local signer
// is attached. Structured as a monkey-patch on window.openPublishOverlay so
// EVERY entry point (post-genesis "Publish for $1 in DEA" button, Katalogos
// tab's Publish links, Stoa row buttons) routes through the real path.
// Falls back to the mockup's illustrative flow when no signer is present.
function wirePublishInterceptor(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  const originalOverlay = w.openPublishOverlay;
  if (typeof originalOverlay !== 'function' || w.__potidaeaPublishWrapped) return;
  w.openPublishOverlay = function wrappedPublish(sym: string) {
    if (!localSigner) return originalOverlay.apply(this, arguments as unknown as unknown[]);
    void runRealPublish(sym);
  };
  w.__potidaeaPublishWrapped = true;
}

async function runRealPublish(sym: string): Promise<void> {
  if (!localSigner) return;
  const mba = KNOWN_MBAS[sym];
  if (!mba) { alert('Cannot publish ' + sym + ': no local metadata for this ticker'); return; }

  // Show a lightweight scrim so the user sees what's happening (the mockup's
  // rich publish overlay has ~500 lines of preflight UI — we shortcut to a
  // single-modal broadcast until we port that surface. Errors surface here.)
  const scrim = document.createElement('div');
  scrim.className = 'wc-scrim'; scrim.id = 'wc-publish-scrim';
  scrim.innerHTML =
    '<div class="wc-modal">' +
      '<div class="wc-modal-h">Publish ' + escHtml(sym) + ' to Katalogos</div>' +
      '<p class="wc-modal-sub">Inscribes the category on-chain in the shared registry (chipnet v1). Fee: <b>~$1 in DEA</b> (231 atomic units at chipnet oracle rate).</p>' +
      '<div id="wc-pub-body"><div style="font-family:var(--font-mono);font-size:11px;color:var(--muted)">Building + broadcasting…</div></div>' +
      '<div class="wc-actions" style="margin-top:14px">' +
        '<button class="wc-cancel" id="wc-pub-close">Close</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(scrim);
  const body = scrim.querySelector('#wc-pub-body') as HTMLElement;
  const closeBtn = scrim.querySelector('#wc-pub-close') as HTMLButtonElement;
  closeBtn.addEventListener('click', () => scrim.remove());

  try {
    // Pull the display metadata off the mockup's ASSETS row for this
    // ticker (Artemision-created assets stash their glyph/name/icon
    // there via app.html wizBroadcast + genesis.ts). Fall back to bare
    // ticker if the row is gone.
    const assetRow = window.ASSETS?.find((a) => a.sym === sym) as
      | { name?: string; iconSha256?: string; iconPath?: string }
      | undefined;
    const displayName = assetRow?.name?.split(' · ')[0];
    // Only inscribe icon chunks when both sha + a fetchable URL are
    // present — the mockup can transiently hold an s3://bns/… fake path
    // (before upload completes), and publishing THAT would put an
    // unfetchable string on-chain forever.
    const iconPath = assetRow?.iconPath;
    const iconOk = !!(assetRow?.iconSha256 && iconPath && /^https?:\/\//.test(iconPath));
    // Template-aware payload: Share MBA (0x01) publishes the company/
    // distribution + split; Artemision (0x00 / undefined) publishes the
    // founder LB. Common fields (ticker/name/icon) ride on top per DVMB.
    const meta: PublishMetadata = mba.templateId === 0x01 ? {
      templateId: TEMPLATE_DEVIANT_SHARE_MBA,
      rateSats: mba.rateSats,
      feeBps: mba.founderFeeBps,   // slot reuse from Share's genesis stashing
      companySplitBps: mba.companySplitBps,
      companyLockingBytecodeHex: mba.founderLockingBytecodeHex,     // Share stashes company LB here
      distributionLockingBytecodeHex: mba.distributionLockingBytecodeHex,
      ticker: mba.ticker,
      name: displayName,
      iconSha256Hex: iconOk ? assetRow?.iconSha256 : undefined,
      iconPath:      iconOk ? iconPath           : undefined,
    } : {
      templateId: TEMPLATE_ARTEMISION_MINT_FT,
      rateSats: mba.rateSats,
      founderFeeBps: mba.founderFeeBps,
      founderLockingBytecodeHex: mba.founderLockingBytecodeHex,
      ticker: mba.ticker,
      name: displayName,
      iconSha256Hex: iconOk ? assetRow?.iconSha256 : undefined,
      iconPath:      iconOk ? iconPath           : undefined,
    };
    const res = await publishViaLocalSigner(localSigner, mba.categoryId, meta);
    logTx({ kind: 'publish', ticker: sym, amount: String(res.feeDea) + ' DEA fee', txid: res.txid });
    body.innerHTML =
      '<div style="color: var(--acid); font-family: var(--font-mono); font-size: 12px; line-height: 1.7">' +
        '✓ Published on chipnet<br>' +
        '<span style="color: var(--muted); font-size: 10px">tx</span> <span id="pubReceiptTx"></span><br>' +
        '<span style="color: var(--muted); font-size: 10px">category</span> <span style="word-break: break-all">' + escHtml(res.targetCategoryId) + '</span><br>' +
        '<span style="color: var(--muted); font-size: 10px">fee</span> ' + String(res.feeDea) + ' atomic DEA' +
      '</div>' +
      '<p class="wc-modal-sub" style="margin-top:12px">Every wallet reading Katalogos will see this listing from the next block. Refresh Potidaea to pick it up in the on-chain read.</p>';
    const pubTxEl = document.getElementById('pubReceiptTx');
    if (pubTxEl) renderTxidWithActions(pubTxEl, res.txid);
    // Refresh the on-chain read so the banner count updates. That in turn
    // triggers paintMyAssets so the row flips from "Local only" to
    // "Listed on Katalogos" without a page reload.
    void refreshRegistry();
  } catch (e) {
    const code = (e instanceof PublishError) ? e.code : 'other';
    const msg = e instanceof Error ? e.message : String(e);
    body.innerHTML =
      '<div class="wc-err">✗ ' + escHtml(msg) + '</div>' +
      (code === 'insufficient-dea'
        ? '<p class="wc-modal-sub" style="margin-top:10px">Chipnet DEA is the fee token. To publish, this wallet needs at least the required atomic units of DEA — send some to <code style="font-family:var(--font-mono);font-size:10px;color:var(--acid);word-break:break-all">' + escHtml(localSigner!.cashaddr) + '</code> from a wallet that has DEA, or ask for a chipnet DEA faucet drop.</p>'
        : '');
  }
}

// Wizard file-input auto-upload. Hooks the mockup's #asIconFile so as soon
// as a user picks an image, we fire the Sia upload in parallel with the
// mockup's own preview/hash work. On success we stash the real URL where
// runArtemisionGenesis + runRealPublish look for it, and swap the review-
// sheet "will store at" line from the s3://bns/katalogos/… placeholder
// over to the real https://navigate.st/… URL. On failure we silently keep
// the local preview and the genesis-time upload fallback still fires.
function wireIconAutoUpload(): void {
  const input = document.getElementById('asIconFile') as HTMLInputElement | null;
  if (!input) return;
  input.addEventListener('change', () => {
    const f = input.files?.[0];
    if (!f) return;
    void handleIconFileUpload(f);
  });
}

async function handleIconFileUpload(f: File): Promise<void> {
  const pathEl = document.getElementById('iconPath');
  if (pathEl) pathEl.textContent = '↑ uploading to Sia…';
  try {
    const bytes = new Uint8Array(await f.arrayBuffer());
    const upload = await uploadIcon(bytes, f.type);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    w.wizIconUploadedUrl = upload.url;
    w.wizIconUploadedSha = upload.sha256Hex;
    if (pathEl) pathEl.textContent = upload.url;
    // Also mirror onto the ASSETS row for the current wizard ticker in
    // case the user has already stepped through to Broadcast — publish
    // will pick these up from either wizIconUploadedUrl or the ASSETS row.
    const sym = (document.getElementById('asSym') as HTMLInputElement | null)?.value?.trim();
    if (sym) {
      const row = window.ASSETS?.find((a) => a.sym === sym) as { iconSha256?: string; iconPath?: string } | undefined;
      if (row) { row.iconSha256 = upload.sha256Hex; row.iconPath = upload.url; }
    }
    console.log('[icon] auto-uploaded', upload.bytes, 'bytes →', upload.url);
  } catch (e) {
    const code = e instanceof IconUploadError ? e.code : 'other';
    if (pathEl) pathEl.textContent = '✗ upload failed (' + code + ') — will retry at Broadcast';
    console.warn('[icon] auto-upload skipped:', e instanceof Error ? e.message : e);
  }
}

// ── Orbital-Design 3-column layout (EtherDelta shape, Potidaea skin) ────
// Left column: assets browser (all discovered + local + seed) with search,
// filter chips (All / Mine / Listed), and click-to-set-`to` behaviour.
// Right column: my holdings (rows with bal > 0) stacked above the tx log.
// The swap card + info panels in the middle are untouched.
//
// All three panels re-render on any of:
//   • boot                (initial paint)
//   • wallet attach/detach (localSigner change)
//   • balance refresh      (paintBalance)
//   • registry discovery   (refreshRegistry)
//   • local tx log update  (logTx)
// The wiring is monkey-patch-style hooks into those existing entry points.

interface TxLogEntry {
  ts: number;
  kind: 'genesis' | 'publish' | 'claim' | 'redeem';
  ticker: string;
  /** Swap direction — populated on claim/redeem so the log can render
   *  a From/To split. Older records may lack these; the renderer falls
   *  back to `ticker` alone. */
  fromSym?: string;
  toSym?: string;
  /** Independent display amounts for each leg — "0.01 BCH", "10,000 ASM".
   *  Replaces the compound `amount` string; still stored alongside for
   *  backward compatibility with rows written before this split. */
  fromAmount?: string;
  toAmount?: string;
  amount?: string;          // legacy — display-form ("1000 ASM", etc.)
  txid: string;
  note?: string;
}

const TX_LOG_KEY = 'deviant.potidaea.tx-log.v1';
const TX_LOG_MAX = 60;

function readTxLog(): TxLogEntry[] {
  try {
    const raw = localStorage.getItem(TX_LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch { return []; }
}

function logTx(entry: Omit<TxLogEntry, 'ts'>): void {
  const list = readTxLog();
  list.unshift({ ts: Date.now(), ...entry });
  const trimmed = list.slice(0, TX_LOG_MAX);
  try { localStorage.setItem(TX_LOG_KEY, JSON.stringify(trimmed)); } catch { /* noop */ }
  paintOdTxLog();
}

// Asset-list tabs — Favorites (★) / All (BCH-family) / FIATm (later).
// Under "All" the user picks a pairing base (BCH · DEA) from a small chip
// strip below the tabs — that base becomes the "from" (or "to") side of
// every listed row, formatted as `TICKER/BASE`. Selecting a base HIDES
// the base itself from the list to avoid a same-asset row (no BCH/BCH).
type OdAssetTab = 'favs' | 'all' | 'fiatm';
let odAssetTab: OdAssetTab = 'all';
let odBase: string = 'BCH';
// Remember the last-picked target per base so switching base tabs does not
// scramble the pair. When the user selects a row while base=DEA, we stash
// { DEA: 'BCH' } — flipping the base to BCH and back to DEA then restores
// the DEA/BCH pair rather than dragging the previous base's `to` across.
const odLastToPerBase: Record<string, string> = {};
// Tx-log tabs — trades / creates / all. Last-selected tab persists in
// localStorage so it doesn't reset every load (user request — the "all"
// tab is common for creators who mix mint + swap sessions).
type OdTxTab = 'trades' | 'creates' | 'all';
const OD_TX_TAB_KEY = 'deviant.potidaea.tx-tab.v1';
function readInitialTxTab(): OdTxTab {
  try {
    const raw = localStorage.getItem(OD_TX_TAB_KEY);
    if (raw === 'trades' || raw === 'creates' || raw === 'all') return raw;
  } catch { /* private mode */ }
  return 'trades';
}
let odTxTab: OdTxTab = readInitialTxTab();

const FAVORITES_KEY = 'deviant.potidaea.favorites.v1';
function readFavorites(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch { return new Set(); }
}
function toggleFavorite(sym: string): void {
  const set = readFavorites();
  if (set.has(sym)) set.delete(sym); else set.add(sym);
  try { localStorage.setItem(FAVORITES_KEY, JSON.stringify([...set])); } catch { /* noop */ }
}

/** Kick off an immediate balance refresh and give the button a brief
 *  spin animation as visual feedback. Falls through gracefully if no
 *  wallet is attached (refreshBalance itself early-returns). */
function wireRefreshButton(id: string): void {
  const btn = document.getElementById(id);
  if (!btn) return;
  btn.addEventListener('click', () => {
    btn.classList.add('spin');
    setTimeout(() => btn.classList.remove('spin'), 800);
    void refreshBalance();
  });
}

function wireOdLayout(): void {
  const searchEl = document.getElementById('odAssetsSearch') as HTMLInputElement | null;
  searchEl?.addEventListener('input', () => paintOdAssets());
  // Refresh buttons — od-botright panel + v-balances view header
  wireRefreshButton('odRefreshBal');
  wireRefreshButton('balRefreshBtn');

  // Asset-list tabs — favs / all / fiatm
  document.querySelectorAll('#odAssets .od-tab[data-al-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('#odAssets .od-tab[data-al-tab]').forEach((b) => b.classList.remove('on'));
      btn.classList.add('on');
      odAssetTab = (btn.getAttribute('data-al-tab') as OdAssetTab) || 'all';
      // Base-strip visible when All or ★ is selected; hidden under FIATm
      // (its own base row lands in a later release).
      const strip = document.getElementById('odBaseStrip');
      if (strip) strip.style.display = (odAssetTab === 'fiatm') ? 'none' : '';
      paintOdAssets();
    });
  });
  // Base-currency chip strip — BCH · DEA.
  // Switching the base is the "pair-currency" affordance, so it must
  // ALSO update the swap card's FROM leg so the main panel reflects the
  // choice immediately (previously only the list re-rendered — the swap
  // card kept whatever from/to the user last had, which felt broken).
  document.querySelectorAll('#odAssets .od-base').forEach((btn) => {
    btn.addEventListener('click', () => {
      // Stash the pair the user was on so switching back to the current
      // base later restores it (the row click also stashes on every pick,
      // this covers the initial state before any row click).
      if (window.to && window.to !== odBase) odLastToPerBase[odBase] = window.to;

      document.querySelectorAll('#odAssets .od-base').forEach((b) => b.classList.remove('on'));
      btn.classList.add('on');
      odBase = btn.getAttribute('data-base') || 'BCH';
      window.from = odBase;

      // Restore the last-picked target for this base, if any. If not,
      // default to the FIRST TRADING PAIR — i.e. the first asset that is
      // neither the current base nor the "other" base (BCH ↔ DEA). Under
      // DEA base that means "skip BCH and land on ASM"; under BCH base
      // that means "skip DEA and land on ASM". A pair like BCH/DEA has
      // no covenant path anyway (see wireSwapConfirmInterceptor), so
      // defaulting into it would put the user on a broken pair.
      const remembered = odLastToPerBase[odBase];
      const assetSyms = new Set((window.ASSETS ?? []).map((a: { sym: string }) => a.sym));
      const symList: string[] = (window.ASSETS ?? []).map((a: { sym: string }) => a.sym);
      const otherBase = odBase === 'BCH' ? 'DEA' : 'BCH';
      const firstMba = symList.find((s) => s !== odBase && s !== otherBase);
      const firstAny = symList.find((s) => s !== odBase);
      const nextTo =
        (remembered && remembered !== odBase && assetSyms.has(remembered)) ? remembered :
        firstMba ??
        firstAny ??
        (odBase === 'BCH' ? 'ASM' : 'BCH');
      window.to = nextTo;

      try { window.recalc?.(); } catch { /* noop */ }
      updateAmountPlaceholder();
      paintOdAssets();
      const t = document.getElementById('odActTitle');
      if (t) t.textContent = 'Activity · ' + nextTo + '/' + odBase;
      void paintOdPublicActivity();
    });
  });

  // Tx-log tabs. On boot, sync the visual "on" state to the persisted
  // choice so the user lands on the tab they last used, not always
  // "trades".
  document.querySelectorAll('.od-ccbot .od-tab[data-mt-tab]').forEach((btn) => {
    const tab = btn.getAttribute('data-mt-tab');
    btn.classList.toggle('on', tab === odTxTab);
    btn.addEventListener('click', () => {
      document.querySelectorAll('.od-ccbot .od-tab[data-mt-tab]').forEach((b) => b.classList.remove('on'));
      btn.classList.add('on');
      odTxTab = (btn.getAttribute('data-mt-tab') as OdTxTab) || 'trades';
      try { localStorage.setItem(OD_TX_TAB_KEY, odTxTab); } catch { /* noop */ }
      paintOdTxLog();
    });
  });

  paintOdAssets();
  paintOdHoldings();
  paintOdTxLog();
  void paintOdPublicActivity();
}

interface OdAssetRow { sym: string; name?: string; glyph: string; iconUrl?: string; rate: number; bal: number; templateId?: number; isMine: boolean; isListed: boolean; }

function collectOdRows(): OdAssetRow[] {
  const assets = window.ASSETS ?? [];
  const registryCats = new Set((registryEntries ?? []).map((e) => e.categoryId));
  const localCats = new Set(Object.values(loadLocalKnownMbas()).map((m) => m.categoryId));
  return assets.map((a) => {
    const km = KNOWN_MBAS[a.sym];
    const isMine = km ? localCats.has(km.categoryId) : false;
    const isListed = km ? registryCats.has(km.categoryId) : (a.sym === 'BCH' || a.sym === 'ASM' || a.sym === 'ASM2');
    return {
      sym: a.sym,
      name: a.name?.split(' · ')[0],
      glyph: a.glyph,
      iconUrl: a.iconUrl,
      rate: a.rate,
      bal: a.bal,
      templateId: km?.templateId,
      isMine, isListed,
    };
  });
}

function tplBadge(tpl?: number): string {
  if (tpl === 0x01) return '<span class="od-tbadge share">Share</span>';
  if (tpl === 0x02) return '<span class="od-tbadge oracle">Oracle</span>';
  return '';
}

function agInner(row: { iconUrl?: string; glyph: string }): string {
  if (row.iconUrl) return '<img src="' + row.iconUrl + '" alt="">';
  return escHtml(row.glyph);
}

function paintOdAssets(): void {
  const listEl = document.getElementById('odAssetsList');
  if (!listEl) return;
  const search = ((document.getElementById('odAssetsSearch') as HTMLInputElement | null)?.value ?? '').trim().toLowerCase();
  const favs = readFavorites();
  let rows = collectOdRows();

  // FIATm tab — placeholder until the FIATμ family covenants land in
  // Potidaea. Hides the list + base-strip is already hidden by the tab
  // handler, so this branch just paints the "coming soon" empty state.
  if (odAssetTab === 'fiatm') {
    listEl.innerHTML = '<div class="od-empty">FIATμ family lands in a later release — <b>USDm · EURm · GBPm · JPYm · INRm</b> (BCH-backed sound-fiat MBAs).</div>';
    return;
  }

  // ★ Favorites tab — only starred assets, still paired against the current
  // base. Favoriting BCH doesn't show a BCH/BCH row (the base gate below
  // filters it out) — the star travels with the asset, not the pair.
  if (odAssetTab === 'favs') {
    rows = rows.filter((r) => favs.has(r.sym));
    if (!rows.length && !search) {
      listEl.innerHTML = '<div class="od-empty">No favorites yet.<br>Tap ☆ on any row to add.</div>';
      return;
    }
  }

  // BASE GATE — exclude:
  //   1. the current base itself (never emit BCH/BCH or DEA/DEA); and
  //   2. the OTHER base (BCH ↔ DEA cross has no swap covenant — DEA is
  //      the platform fee-token, not an MBA. Listing "BCH/DEA" under DEA
  //      base would highlight a broken pair as the top row.)
  // Net: under any base the list contains only the MBAs the base can
  // actually pair with.
  const otherBase = odBase === 'BCH' ? 'DEA' : 'BCH';
  rows = rows.filter((r) => r.sym !== odBase && r.sym !== otherBase);

  if (search) rows = rows.filter((r) => r.sym.toLowerCase().includes(search) || r.name?.toLowerCase().includes(search));
  if (!rows.length) { listEl.innerHTML = '<div class="od-empty">No matches</div>'; return; }

  const activeTo = window.to;
  listEl.innerHTML = rows.map((r) => {
    const balStr = r.bal > 0 ? (r.sym === 'BCH' ? r.bal.toFixed(4) : Math.round(r.bal).toLocaleString('en-US')) : '—';
    const balClass = r.bal > 0 ? '' : ' class="zero"';
    const starOn = favs.has(r.sym);
    const pairLabel = escHtml(r.sym) + '<span class="od-slash">/</span>' + escHtml(odBase);
    // Sub-line — only render when it adds information beyond the ticker.
    // Hides on:
    //   • no name at all
    //   • name === ticker           (e.g. ASSETS row "ASM · chipnet" → "ASM")
    //   • name === "MBA" fallback   (generic placeholder for unknown assets)
    //   • ticker starts with "K:"   (short-form category label; sub would
    //                                just repeat it)
    const rawSub = r.name ? r.name : (r.sym === 'BCH' ? 'Bitcoin Cash' : '');
    const showSub = !!rawSub && rawSub !== r.sym && rawSub !== 'MBA' && !r.sym.startsWith('K:');
    return '<div class="od-arow ' + (r.sym === activeTo ? 'on' : '') + '" data-sym="' + escAttr(r.sym) + '">' +
      '<span class="od-star ' + (starOn ? 'on' : '') + '" data-fav="' + escAttr(r.sym) + '" title="' + (starOn ? 'Un-favorite' : 'Add to favorites') + '">' + (starOn ? '★' : '☆') + '</span>' +
      '<span class="od-ag">' + agInner(r) + '</span>' +
      '<div class="od-meta"><div class="sym">' + pairLabel + '</div>' +
      (showSub ? '<div class="sub">' + escHtml(rawSub) + '</div>' : '') + '</div>' +
      '<div class="od-bal"><span' + balClass + '>' + balStr + '</span></div>' +
    '</div>';
  }).join('');

  // Star toggles — stopPropagation so a star tap doesn't also fire the row click.
  listEl.querySelectorAll('.od-star[data-fav]').forEach((star) => {
    star.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const sym = (star as HTMLElement).getAttribute('data-fav'); if (!sym) return;
      toggleFavorite(sym);
      paintOdAssets();
    });
  });
  // Row click → set the pair as from=BASE, to=TICKER (buy TICKER with BASE).
  // Also stash the pick under the current base so switching bases and
  // returning restores THIS pair rather than the other base's last state.
  listEl.querySelectorAll('.od-arow').forEach((row) => {
    row.addEventListener('click', () => {
      const sym = row.getAttribute('data-sym'); if (!sym) return;
      window.from = odBase;
      window.to = sym;
      odLastToPerBase[odBase] = sym;
      try { window.recalc?.(); } catch { /* noop */ }
      paintOdAssets();
      updateAmountPlaceholder();
      const t = document.getElementById('odActTitle');
      if (t) t.textContent = 'Activity · ' + sym + '/' + odBase;
      void paintOdPublicActivity();
    });
  });
}

function paintOdHoldings(): void {
  const holdings = document.getElementById('odHoldingsList');
  const totalEl = document.getElementById('odTotalUsd');
  if (!holdings) return;
  const rows = collectOdRows().filter((r) => r.bal > 0);
  if (!rows.length) { holdings.innerHTML = '<div class="od-empty">' + (localSigner ? 'No holdings yet' : 'No wallet attached') + '</div>'; if (totalEl) totalEl.textContent = '≈ $0'; return; }
  // The panel's ≈ $X header shows SPENDABLE BCH value only (BCH row).
  // MBA holdings display their own per-row backing value beneath the
  // amount, but they don't inflate the panel-level total — a claimed
  // MBA doesn't give the user extra spendable BCH; the BCH is locked
  // in the covenant until redeemed.
  let spendableBch = 0;
  const parts = rows.map((r) => {
    const valBch = r.sym === 'BCH' ? r.bal : (r.bal * r.rate / 1e8);
    if (r.sym === 'BCH') spendableBch += valBch;
    const displayBal = r.sym === 'BCH' ? r.bal.toFixed(4) : Math.round(r.bal).toLocaleString('en-US');
    const usd = (valBch * bchUsd()).toLocaleString('en-US', { maximumFractionDigits: 2 });
    return '<div class="od-hrow">' +
      '<span class="od-ag">' + agInner(r) + '</span>' +
      '<span class="sym">' + escHtml(r.sym) + '</span>' +
      '<span class="amt">' + displayBal + '<span class="usd">≈ $' + usd + '</span></span>' +
    '</div>';
  });
  holdings.innerHTML = parts.join('');
  if (totalEl) totalEl.textContent = '≈ $' + (spendableBch * bchUsd()).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function relTime(ts: number): string {
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 60) return secs + 's';
  if (secs < 3600) return Math.floor(secs / 60) + 'm';
  if (secs < 86400) return Math.floor(secs / 3600) + 'h';
  return Math.floor(secs / 86400) + 'd';
}

const OD_TXLOG_LIMIT = 30;   // rows shown in the personal tx-history panel

function paintOdTxLog(): void {
  const el = document.getElementById('odTxLog'); if (!el) return;
  let log = readTxLog();
  // Tab filter:
  //   trades  → claim + redeem (actual swaps)
  //   creates → genesis + publish (asset lifecycle)
  //   all     → everything
  if (odTxTab === 'trades')  log = log.filter((e) => e.kind === 'claim' || e.kind === 'redeem');
  else if (odTxTab === 'creates') log = log.filter((e) => e.kind === 'genesis' || e.kind === 'publish');
  if (!log.length) {
    el.innerHTML = '<div class="od-empty">No ' + (odTxTab === 'all' ? 'transactions' : odTxTab) + ' yet</div>';
    return;
  }
  // Cap render count so the panel stays a fixed size; the list scrolls
  // to show a few off-screen entries. Older-than-N rows are dropped from
  // the paint (storage still keeps TX_LOG_MAX for reference).
  log = log.slice(0, OD_TXLOG_LIMIT);
  // Personal tx log is a market-trades-style feed with a From/To split:
  //     Pair       From          To            Time
  //     BCH/ASM    0.01 BCH      10,000 ASM    00:40:48   (buy = green)
  //     ASM/BCH    500 ASM       0.005 BCH     00:39:48   (sell = red)
  // Genesis + Publish keep their descriptive labels in the Pair slot
  // and only fill the From column ("Static" / etc.), leaving To empty.
  //
  // Amounts are pulled from fromAmount/toAmount when present; older
  // records fall back to parsing the compound `amount` string so a
  // pre-split history still reads sensibly.
  const header =
    '<div class="od-mhead od-mhead-4">' +
      '<span class="m-price">Pair</span>' +
      '<span class="m-from">From</span>' +
      '<span class="m-to">To</span>' +
      '<span class="m-time">Time</span>' +
    '</div>';
  const body = log.map((e) => {
    let pairLabel: string;
    let fromAmount = '';
    let toAmount = '';
    let sideClass = '';
    if (e.kind === 'claim' || e.kind === 'redeem') {
      const fromS = e.fromSym ?? (e.kind === 'claim' ? 'BCH' : e.ticker);
      const toS   = e.toSym   ?? (e.kind === 'claim' ? e.ticker : 'BCH');
      pairLabel = fromS + '/' + toS;
      sideClass = e.kind === 'claim' ? '' : 'sell';
      if (e.fromAmount && e.toAmount) {
        fromAmount = e.fromAmount;
        toAmount = e.toAmount;
      } else if (e.amount) {
        // Legacy fallback — split a "0.005 BCH ← burned 500 ASM" style
        // string, or fall back to just one side.
        const m = /^(.*?)\s*(?:←|<-|\||→|->)\s*(?:burned\s*)?(.*)$/.exec(e.amount);
        if (m) { toAmount = m[1].trim(); fromAmount = m[2].trim(); }
        else if (e.kind === 'claim') { toAmount = e.amount; }
        else { fromAmount = e.amount; }
      }
    } else if (e.kind === 'genesis') {
      pairLabel = 'Genesis ' + e.ticker;
      fromAmount = e.amount ?? '';
    } else {
      pairLabel = 'Publish ' + e.ticker;
      fromAmount = e.amount ?? '';
    }
    const time = formatOdClock(Math.floor(e.ts / 1000));
    // Trim trailing-zero BCH strings so a redeem shows "0.1 BCH" rather
    // than "0.10000000 BCH" — matches the compactness of the MBA amounts.
    const fromDisplay = fromAmount ? trimTrailingZeros(fromAmount) : '—';
    const toDisplay   = toAmount   ? trimTrailingZeros(toAmount)   : '—';
    return '<div class="od-mrow od-mrow-4 ' + sideClass + '" data-txid="' + escAttr(e.txid) + '" title="' + escAttr(e.txid) + '">' +
      '<span class="m-price">' + escHtml(pairLabel) + '</span>' +
      '<span class="m-from">' + escHtml(fromDisplay) + '</span>' +
      '<span class="m-to">' + escHtml(toDisplay) + '</span>' +
      '<span class="m-time">' + escHtml(time) + '</span>' +
    '</div>';
  }).join('');
  el.innerHTML = header + body;
  el.querySelectorAll('.od-mrow').forEach((row) => {
    row.addEventListener('click', () => {
      const txid = row.getAttribute('data-txid'); if (!txid) return;
      // Chipnet block explorer — same one the wallet uses.
      window.open('https://chipnet.imaginary.cash/tx/' + txid, '_blank', 'noopener,noreferrer');
    });
  });
}

function paintOdAll(): void { paintOdAssets(); paintOdHoldings(); paintOdTxLog(); void paintOdPublicActivity(); }

// ── Public activity panel — real chipnet history for the current pair ─────
// The top-left "Activity · <pair>" panel used to be an empty placeholder.
// Fill it with `blockchain.address.get_history` on the MBA covenant address
// of whichever side of the pair is an MBA (claim txs spend + rebind the
// covenant; redeems don't touch it, so this is the claim-side feed — a
// real public trace for anyone's swaps on that pair).
//
// Cheap cache keyed by contract address so pair-flipping doesn't re-fetch
// the same feed within CACHE_MS.
interface OdActivityRow {
  txid: string;
  height: number;
  /** 'buy' = BCH → MBA (claim); 'sell' = MBA → BCH (redeem). Rows fetched
   *  from the covenant address are all buys; sells are merged in from the
   *  user's own tx log until a chain-wide token-category history feed is
   *  available (Fulcrum does not currently expose one — verified on
   *  chipnet.imaginary.cash:50004: token.category.get_history, etc. all
   *  respond "Unsupported request"). */
  side: 'buy' | 'sell';
  /** Populated by a background verbose-tx fetch. */
  amountUnits?: bigint;
  time?: number;   // unix seconds; from block header via verbose tx
}
const odActivityCache = new Map<string, { at: number; rows: OdActivityRow[] }>();
/** Per-txid cache of (amountUnits, time) — survives pair switches so a
 *  round-trip back to ASM/BCH re-uses the same enrichments. */
const odActivityTxCache = new Map<string, { amountUnits: bigint; time: number }>();
const OD_ACTIVITY_CACHE_MS = 30_000;
const OD_ACTIVITY_LIMIT = 30;   // rows shown in the public-activity panel
const OD_ACTIVITY_ENRICH_CONCURRENCY = 4; // simultaneous verbose-tx fetches

function pairMbaTicker(): string | null {
  const from = window.from, to = window.to;
  if (from && from in KNOWN_MBAS) return from;
  if (to && to in KNOWN_MBAS) return to;
  return null;
}

/** Parse an FT unit count out of a log's display-form fromAmount like
 *  "10,000 ASM" or "10000 ASM" — trims commas + whitespace, then reads
 *  the leading digit run. Returns undefined if the input doesn't shape
 *  like an integer amount for `ticker` (so the renderer falls back to
 *  its own placeholder). Fractional amounts (unusual for FT) are floored
 *  to whole units since MBAs are integer-valued. */
function parseRedeemUnitsFromLog(fromAmount: string | undefined, ticker: string): bigint | undefined {
  if (!fromAmount) return undefined;
  // Ticker suffix is optional in older records; either " ASM" or nothing.
  const stripped = fromAmount.replace(/,/g, '').replace(new RegExp('\\s*' + ticker + '\\s*$', 'i'), '').trim();
  const digits = stripped.match(/^(\d+)/);
  if (!digits) return undefined;
  try { return BigInt(digits[1]); } catch { return undefined; }
}

async function paintOdPublicActivity(): Promise<void> {
  const el = document.getElementById('odActBody');
  if (!el) return;
  const ticker = pairMbaTicker();
  if (!ticker) {
    el.innerHTML = '<div class="od-empty">Pick a token pair to see its public activity</div>';
    return;
  }
  const m = KNOWN_MBAS[ticker];
  const addr = m.contractAddress;
  if (!addr) {
    el.innerHTML = '<div class="od-empty">No covenant address for ' + escHtml(ticker) + '</div>';
    return;
  }

  // Cache-hit → render synchronously; cache-miss → paint "loading" and fetch.
  const now = Date.now();
  const cached = odActivityCache.get(addr);
  if (cached && now - cached.at < OD_ACTIVITY_CACHE_MS) {
    renderOdActivityRows(el, ticker, cached.rows);
    return;
  }

  el.innerHTML = '<div class="od-empty">Loading public activity for ' + escHtml(ticker) + '…</div>';

  try {
    if (!currentClient) currentClient = await ElectrumClient.connectAny();
    const raw = await currentClient.request<Array<{ tx_hash: string; height: number }>>(
      'blockchain.address.get_history', [addr],
    );
    // Most recent first — Fulcrum returns oldest→newest, so reverse.
    const buyRows: OdActivityRow[] = (raw ?? [])
      .slice(-OD_ACTIVITY_LIMIT * 2)
      .reverse()
      .slice(0, OD_ACTIVITY_LIMIT)
      .map((r) => ({ txid: r.tx_hash, height: r.height, side: 'buy' as const }));
    // Sells: read from the user's own tx log. Potidaea redeems burn FT
    // in-place — the redeem tx never touches the covenant address, and
    // Fulcrum on chipnet.imaginary.cash doesn't expose a chain-wide
    // token-category history RPC (token.category.get_history etc. return
    // "Unsupported request"), so we can't discover other users' redeems
    // yet. Our own redeems are still a real and useful signal for the
    // pair, and they render red so the panel isn't buy-only.
    const sellRows: OdActivityRow[] = readTxLog()
      .filter((e) => e.kind === 'redeem' && (e.ticker === ticker || e.fromSym === ticker))
      .map((e) => ({
        txid: e.txid,
        height: 0,                     // unknown from local log; enrichment will fill in
        side: 'sell' as const,
        time: Math.floor(e.ts / 1000), // already have a local timestamp — good default
        // Parse the redeemed unit count from the log's display-form
        // fromAmount ("10,000 ASM"). Strip commas/whitespace and cut at
        // the first non-digit so " 10,000 ASM" becomes 10000. Falls back
        // silently on parse errors — the renderer knows how to show '—'.
        amountUnits: parseRedeemUnitsFromLog(e.fromAmount, ticker),
      }));

    // Merge and sort: newest first. For buys we use `time` (from block
    // header, populated by enrichment). Until enrichment fills it, we
    // fall back to `height` as an ordering proxy. Sells always have a
    // local `ts`, so their `time` is set already.
    const merged = [...buyRows, ...sellRows]
      .sort((a, b) => (b.time ?? b.height) - (a.time ?? a.height))
      .slice(0, OD_ACTIVITY_LIMIT);
    odActivityCache.set(addr, { at: now, rows: merged });
    // First: hydrate any rows whose (amount, time) are already cached from
    // a prior look. The rest paint with '…' placeholders and get enriched
    // asynchronously below.
    for (const r of merged) {
      const cached = odActivityTxCache.get(r.txid);
      if (cached) { r.amountUnits = cached.amountUnits; r.time = cached.time; }
    }
    if (pairMbaTicker() === ticker) renderOdActivityRows(el, ticker, merged);
    // Kick off background enrichment for anything still missing (unless
    // the pair changed, in which case bail — the new pair paints itself).
    // Pass the FULL merged list so the enrichment repaint preserves the
    // sell rows; enrichOdActivityRows internally filters to rows that
    // still need (amount, time), so already-hydrated sells are a no-op.
    void enrichOdActivityRows(ticker, merged, m.categoryId, addr);
  } catch (e) {
    console.warn('[potidaea] public activity fetch failed:', e instanceof Error ? e.message : e);
    // Electrum can't reach us — the buy feed is out of reach, but our own
    // redeems live in localStorage and cost nothing to render. Show them
    // alone with an "offline" hint so the panel isn't a dead placeholder.
    if (pairMbaTicker() !== ticker) return;
    const sellsOnly: OdActivityRow[] = readTxLog()
      .filter((e2) => e2.kind === 'redeem' && (e2.ticker === ticker || e2.fromSym === ticker))
      .map((e2) => ({
        txid: e2.txid,
        height: 0,
        side: 'sell' as const,
        time: Math.floor(e2.ts / 1000),
        amountUnits: parseRedeemUnitsFromLog(e2.fromAmount, ticker),
      }));
    if (sellsOnly.length === 0) {
      el.innerHTML = '<div class="od-empty">Activity feed offline · retry on next pair change</div>';
      return;
    }
    renderOdActivityRows(el, ticker, sellsOnly);
    // Prepend a small notice so the user knows the buy feed is missing.
    const notice = document.createElement('div');
    notice.className = 'od-empty';
    notice.style.fontSize = '9px';
    notice.style.padding = '4px 4px 6px';
    notice.textContent = 'Buy feed offline · showing your redeems only';
    el.insertBefore(notice, el.firstChild);
  }
}

/** Verbose tx shape as Fulcrum returns it (partial — only what we need). */
interface FulcrumVerboseTx {
  txid?: string;
  time?: number;      // block time, unix seconds
  blocktime?: number; // same, some servers name it this
  vout?: Array<{
    value?: number;
    scriptPubKey?: { hex?: string };
    token_data?: { category?: string; amount?: string };
    tokenData?: { category?: string; amount?: string };
  }>;
}

/** Fetch verbose tx per row, extract the FT amount minted to the user +
 *  the block time, and repaint each row as data arrives. Skips rows that
 *  are already enriched (from cache). Concurrency-limited so we don't
 *  blast the endpoint. */
async function enrichOdActivityRows(
  ticker: string,
  rows: OdActivityRow[],
  categoryId: string,
  covenantAddr: string,
): Promise<void> {
  if (!currentClient) return;
  const client = currentClient;

  // Decode covenant address → its scriptPubKey hex, so we can EXCLUDE the
  // covenant's own reservoir output and count only the user-bound FT.
  let covenantLbHex = '';
  try {
    const dec = decodeCashAddress(covenantAddr);
    if (typeof dec !== 'string' && dec.payload) covenantLbHex = bytesToHex(dec.payload);
  } catch { /* ignore — worst case we count the reservoir too */ }

  // Enrichment reads the tx's outputs to find the FT amount minted to a
  // non-covenant address — that's a claim-side signal. Sell rows are
  // redeems that BURN their FT in-place (no covenant output, no FT
  // output to a user), so verbose-tx scanning would report amount = 0
  // and overwrite the amount we already parsed from the local log.
  // Skip them: sell rows keep their local (amount, time) as-is.
  const todo = rows.filter((r) =>
    r.side !== 'sell' && (r.amountUnits === undefined || r.time === undefined),
  );
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < todo.length) {
      const r = todo[cursor++];
      try {
        const verbose = await client.request<FulcrumVerboseTx>('blockchain.transaction.get', [r.txid, true]);
        const time = verbose.time ?? verbose.blocktime ?? 0;
        let amount = 0n;
        for (const v of verbose.vout ?? []) {
          const td = v.token_data ?? v.tokenData;
          if (!td || (td.category ?? '').toLowerCase() !== categoryId.toLowerCase()) continue;
          // Skip the covenant's own reservoir output. Match either direction:
          // for a P2SH32 covenant, decodeCashAddress gives the 32-byte hash
          // and the scriptPubKey hex is `aa20<hash>87` — so the scriptPubKey
          // CONTAINS the hash. Some servers return the scriptPubKey in a
          // shorter form; check both containments to be safe.
          const spk = v.scriptPubKey?.hex?.toLowerCase() ?? '';
          const cov = covenantLbHex.toLowerCase();
          if (cov && spk && (spk.includes(cov) || cov.includes(spk))) continue;
          if (td.amount) amount += BigInt(td.amount);
        }
        if (amount > 0n || time) {
          r.amountUnits = amount;
          r.time = time;
          odActivityTxCache.set(r.txid, { amountUnits: amount, time });
        }
      } catch (e) {
        console.warn('[potidaea] enrich failed for', r.txid.slice(0, 10), e instanceof Error ? e.message : e);
      }
    }
  };
  const workers = Array.from({ length: OD_ACTIVITY_ENRICH_CONCURRENCY }, () => worker());
  await Promise.all(workers);
  // Only repaint if the pair the user is looking at is still this ticker.
  if (pairMbaTicker() === ticker) {
    const el = document.getElementById('odActBody');
    if (el) renderOdActivityRows(el, ticker, rows);
  }
}

function bytesToHex(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}

function formatOdAmount(units: bigint): string {
  const n = Number(units);
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(2) + 'K';
  return n.toLocaleString('en-US');
}

function formatOdPrice(rateSats: bigint): string {
  // rateSats = sats per 1 atomic unit of the MBA. Convert to BCH per unit
  // (rate/1e8). Keep 8 decimals for a stable-width column that reads like
  // a proper exchange quote ("0.00000100 BCH per ASM").
  const bchPerUnit = Number(rateSats) / 1e8;
  if (bchPerUnit >= 0.01) return bchPerUnit.toFixed(6);
  return bchPerUnit.toFixed(8);
}

/** Strip meaningless trailing zeros from a "X.YYY BCH" display string.
 *  "0.10000000 BCH" → "0.1 BCH", "0.12400000 BCH" → "0.124 BCH",
 *  "1.00000000 BCH" → "1 BCH". Non-BCH strings (MBA amounts like
 *  "10,000 ASM") are returned unchanged. */
function trimTrailingZeros(text: string): string {
  return text.replace(/(\d+)\.(\d*?)0+(\s+BCH)\b/gi, (_, whole, frac, unit) => {
    const trimmedFrac = frac.replace(/0+$/, '');
    return whole + (trimmedFrac ? '.' + trimmedFrac : '') + unit;
  });
}

function formatOdClock(unixSec?: number): string {
  if (!unixSec) return '';
  const d = new Date(unixSec * 1000);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function renderOdActivityRows(el: HTMLElement, ticker: string, rows: OdActivityRow[]): void {
  if (!rows.length) {
    el.innerHTML = '<div class="od-empty">No on-chain swaps yet for ' + escHtml(ticker) + '</div>';
    return;
  }
  const m = KNOWN_MBAS[ticker];
  const priceStr = m ? formatOdPrice(m.rateSats) : '—';
  // Buys (BCH → MBA, from covenant address history) render green. Sells
  // (MBA → BCH, from the user's local tx log) render red. Once a
  // chain-wide token-category history feed is available (Fulcrum doesn't
  // expose one today — verified 2026-09-18 on chipnet.imaginary.cash),
  // sells will also come from public data.
  const header =
    '<div class="od-mhead">' +
      '<span class="m-price">Price (BCH)</span>' +
      '<span class="m-amt">Amount (' + escHtml(ticker) + ')</span>' +
      '<span class="m-time">Time</span>' +
    '</div>';
  const body = rows.map((r) => {
    const amountStr = r.amountUnits !== undefined
      ? formatOdAmount(r.amountUnits)
      : (r.height > 0 ? '…' : (r.side === 'sell' ? 'local' : 'mempool'));
    const timeStr = r.time
      ? formatOdClock(r.time)
      : (r.height > 0 ? '#' + r.height : 'now');
    const sideClass = r.side === 'sell' ? ' sell' : '';
    return '<div class="od-mrow' + sideClass + '" data-txid="' + escAttr(r.txid) + '" title="' + escAttr(r.txid) + '">' +
      '<span class="m-price">' + escHtml(priceStr) + '</span>' +
      '<span class="m-amt">' + escHtml(amountStr) + '</span>' +
      '<span class="m-time">' + escHtml(timeStr) + '</span>' +
    '</div>';
  }).join('');
  el.innerHTML = header + body;
  el.querySelectorAll('.od-mrow').forEach((row) => {
    row.addEventListener('click', () => {
      const txid = row.getAttribute('data-txid'); if (!txid) return;
      window.open('https://chipnet.imaginary.cash/tx/' + txid, '_blank', 'noopener,noreferrer');
    });
  });
}

// Decode a `data:image/…;base64,…` URL into raw bytes + content-type. The
// mockup's file picker stores uploaded icons in this form on window.
function dataUrlToBytes(dataUrl: string): { bytes: Uint8Array; contentType: string } {
  const m = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!m) throw new Error('unexpected data-url shape');
  const contentType = m[1];
  const bin = atob(m[2]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return { bytes, contentType };
}

function attachWellKnownIcons(): void {
  const assets = window.ASSETS;
  if (!assets) return;
  const bch = assets.find((a) => a.sym === 'BCH');
  if (bch && !bch.iconUrl) bch.iconUrl = BCH_ICON_DATA_URI;
  // Ensure DEA exists as an ASSETS row so the base-currency chip strip
  // has a valid FROM to point the swap card at. DEA's chipnet rate is
  // 867 sats/atomic (KatalogosRegistryV1 constructor arg — see
  // publish.ts CHIPNET_REGISTRY.deaRateSats).
  if (!assets.find((a) => a.sym === 'DEA')) {
    assets.push({ sym: 'DEA', name: 'Deviant Equilibrium Asset', glyph: 'D', rate: 867, cls: 'DEA', bal: 0, pub: true });
  }
  // Force any already-rendered surface to redraw with the new icon.
  try { window.recalc?.(); } catch { /* noop */ }
  try { window.renderKat?.(); } catch { /* noop */ }
  try { (window as unknown as { renderBalances?: () => void }).renderBalances?.(); } catch { /* noop */ }
}

function zeroAllBalances(): void {
  const assets = window.ASSETS;
  if (!assets) return;
  for (const a of assets) a.bal = 0;
  const bchEl = document.getElementById('chipBch');
  if (bchEl) bchEl.textContent = '0.0000 BCH';
  try { (window as unknown as { renderBalances?: () => void }).renderBalances?.(); } catch { /* noop */ }
  try { window.recalc?.(); } catch { /* noop */ }
  try { window.renderKat?.(); } catch { /* noop */ }
  try { paintOdAll(); } catch { /* noop */ }
}

// ── Default amount placeholder ─────────────────────────────────────────────
// Clears the hard-coded "1000" and installs a live hint that reads
// "≈ $20 in <FROM>" — because 1000 BCH and 1000 ASM are wildly different
// value orders, a single hard default makes no sense across assets. The
// hint is oracle-decorative (bchUsd from CoinGecko / CryptoCompare with a
// fallback constant), not a settlement price — the user still types the
// exact amount they want.
//
// Wire: overrides #amtIn's initial value + placeholder, then wraps
// window.recalc so any FROM change (asset picker, flip button, max click,
// direct input) refreshes the placeholder to the new denomination.

const DEFAULT_USD_TARGET = 20;

function currentFromAsset(): { sym: string; rate: number; bal: number } | null {
  const from = window.from;
  if (!from) return null;
  const a = window.ASSETS?.find((x) => x.sym === from);
  if (!a) return null;
  return { sym: a.sym, rate: a.rate, bal: a.bal };
}

function computeHintForFrom(fromSym: string, fromRate: number): { units: number; label: string } {
  // rate = sats per unit. 1 BCH → 1e8 sats. $20 in USD = 20/bchUsd BCH = 20*1e8/bchUsd sats.
  const targetSats = (DEFAULT_USD_TARGET * 1e8) / bchUsd();
  const units = targetSats / fromRate;
  // Precision heuristic:
  //   BCH        → 6 dp (small fractions matter)
  //   fine MBAs  → 2 dp for large numbers, no dp when > 1000
  //   default    → 4 dp
  let str: string;
  if (fromSym === 'BCH') str = units.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  else if (units >= 1000) str = Math.round(units).toLocaleString('en-US');
  else if (units >= 1) str = units.toFixed(2);
  else str = units.toPrecision(2);
  return { units, label: `e.g. ${str} ${fromSym} (≈ $${DEFAULT_USD_TARGET})` };
}

function updateAmountPlaceholder(): void {
  const inp = document.getElementById('amtIn') as HTMLInputElement | null;
  if (!inp) return;
  const a = currentFromAsset();
  if (!a) { inp.placeholder = 'amount'; return; }
  const { label } = computeHintForFrom(a.sym, a.rate);
  inp.placeholder = label;
}

// Patches the Balances page's USD total. The mockup's own renderBalances
// derives bchUsd from a USDm asset that doesn't exist in ASSETS, so the
// total collapses to "≈ 0 USD". Overwrite the label with a real number
// from our oracle every time renderBalances runs.
function installBalancesUsdPatch(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  const original = w.renderBalances;
  if (typeof original !== 'function' || w.__potidaeaBalancesWrapped) return;
  w.renderBalances = function wrapped() {
    const r = original.apply(this, arguments as unknown as unknown[]);
    try {
      const el = document.getElementById('balTotalUsd');
      const bchEl = document.getElementById('balTotalBch');
      if (el && bchEl) {
        const bch = parseFloat((bchEl.textContent || '').replace(/[^0-9.]/g, '')) || 0;
        const usd = bch * bchUsd();
        el.textContent = '≈ $' + usd.toLocaleString('en-US', { maximumFractionDigits: 2 });
      }
    } catch { /* noop */ }
    return r;
  };
  w.__potidaeaBalancesWrapped = true;
}

function installAmountPlaceholder(): void {
  const inp = document.getElementById('amtIn') as HTMLInputElement | null;
  if (!inp) return;
  // Only clear the hard-coded "1000". Don't touch a value the user has
  // already typed (in the unlikely case boot runs after they've started).
  if (inp.value === '1000') inp.value = '';
  updateAmountPlaceholder();

  // Wrap the mockup's recalc so we run after every state change (asset
  // picker → new from, flip button → swapped from/to, max click →
  // typed value, direct input → user typed). recalc is defined on
  // window via the mockup's non-strict-mode inline script.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  const originalRecalc = w.recalc;
  if (typeof originalRecalc === 'function' && !w.__potidaeaRecalcWrapped) {
    w.recalc = function wrapped() {
      const r = originalRecalc.apply(this, arguments as unknown as unknown[]);
      try { updateAmountPlaceholder(); } catch { /* noop */ }
      return r;
    };
    w.__potidaeaRecalcWrapped = true;
  }
}
// <script type="module"> is deferred, so by the time this file runs the DOM is
// usually already parsed. Handle both cases: run boot immediately if the body
// is present, otherwise wait for DOMContentLoaded.
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

window.potidaeaWc = {
  connect: () => { wc.connect(); },
  disconnect: () => { wc.disconnect(); },
  refreshBalance,
  get cashaddr() { return wc.snapshot.cashaddr; },
};

// Correct-math swap preview — exposed to the mockup's inline recalc so
// the amtOut estimate matches what the covenant actually mints (or
// releases). The mockup's default recalc applies a flat 0.5% fee to the
// FROM amount, then converts. The chain covenant works differently:
//   - CLAIM (BCH → MBA): user pays gross BCH into covenant; receives
//     floor(gross_bits / mba_rate) MBA units. Founder + platform fee are
//     EXTRA BCH outputs deducted from the wallet's plain BCH change, NOT
//     from the mint amount. So the true amtOut is the FULL conversion.
//   - REDEEM (MBA → BCH): user burns units; receives (units × rate -
//     miner_fee) BCH bits. No founder / platform fee.
// This shim is called from the inline mockup script; unknown pairs fall
// back to the mockup's own math so USDm/etc. don't break.
interface SwapNetEstimate {
  net: number;         // whole-unit amount in the TO asset
  kind: 'claim' | 'redeem' | 'unknown';
  feeBch: number;      // extra BCH the user will pay (info panel), 0 for redeem
  totalCostBch: number; // gross BCH the wallet spends (input side, claim only)
}
// Expose bchUsd() to the mockup's inline script so the rate line can
// print a "≈ $X" hint alongside the fixed BCH/MBA rate. Read-only; the
// cached USD/BCH value is refreshed by refreshBchUsd() at boot.
(window as unknown as { potidaeaBchUsd?: () => number }).potidaeaBchUsd = bchUsd;

// Expose KNOWN_MBAS so the Katalogos search can match category ids
// alongside ticker + name. Read-only; the mockup treats it as data.
(window as unknown as { KNOWN_MBAS?: typeof KNOWN_MBAS }).KNOWN_MBAS = KNOWN_MBAS;

/** Min / Med / Max quick-fill amounts for the FROM leg of the swap card.
 *  MIN is the smallest amount the covenant will accept without dust /
 *  fee-floor failure. MAX is what the wallet can actually broadcast
 *  right now (balance discounted for founder + platform + miner fees,
 *  so the receipt matches the review sheet). MED is a geometric midpoint
 *  so heavy and light traders both land on a sensible probe.
 *
 *  Unknown pairs get a `(0, bal/2, bal)` fallback so USDm etc. still
 *  fill something; the swap card's usual clamp then handles any errors. */
interface SwapSizes { min: number; med: number; max: number; kind: 'claim' | 'redeem' | 'unknown' }
(window as unknown as { potidaeaSwapSizes?: (from: string, to: string, balFrom: number, balTo: number) => SwapSizes }).potidaeaSwapSizes = function(from: string, to: string, balFrom: number, _balTo: number): SwapSizes {
  const MINER_FEE_BITS = 3000;
  const DUST_BITS = 546;
  // Claim — from BCH → to MBA
  if (from === 'BCH' && to in KNOWN_MBAS) {
    const m = KNOWN_MBAS[to];
    const rate = Number(m.rateSats);
    const founderBps = Number(m.founderFeeBps);
    const platformBps = 50; // PLATFORM_FEE_V1
    // MIN — dust floor. Covenant requires founder fee ≥ 546 bits, so:
    //   gross_min = ceil(546 × 10000 / founderBps). If founderBps=0, use
    //   miner fee floor instead.
    let minBits: number;
    if (founderBps > 0) {
      minBits = Math.ceil(546 * 10000 / founderBps);
    } else {
      minBits = Math.max(rate, MINER_FEE_BITS + DUST_BITS);
    }
    // MAX — walk backwards from the wallet's BCH balance. Solve
    //   bal ≥ gross + gross×(founderBps+platformBps)/10000 + miner
    // for gross.
    const balBits = Math.max(0, Math.floor(balFrom * 1e8));
    const totalFeeBps = founderBps + platformBps;
    const spendable = balBits - MINER_FEE_BITS;
    // gross × (10000 + totalFeeBps) / 10000 ≤ spendable
    // gross ≤ spendable × 10000 / (10000 + totalFeeBps)
    const grossMax = Math.max(0, Math.floor(spendable * 10000 / (10000 + totalFeeBps)));
    // Snap down to a whole MBA unit so `units × rate == grossMax` (avoids
    // dust remainder inside the covenant that can trip broadcast).
    const unitsMax = Math.floor(grossMax / rate);
    const maxBits = unitsMax * rate;
    // MED — geometric mean of min and max, snapped down to whole units.
    const medBitsRaw = minBits > 0 && maxBits > minBits
      ? Math.floor(Math.sqrt(minBits * maxBits))
      : Math.floor(maxBits / 2);
    const medUnits = Math.floor(medBitsRaw / rate);
    const medBits = medUnits * rate;
    return { min: minBits / 1e8, med: medBits / 1e8, max: maxBits / 1e8, kind: 'claim' };
  }
  // Redeem — from MBA → to BCH
  if (to === 'BCH' && from in KNOWN_MBAS) {
    const m = KNOWN_MBAS[from];
    const rate = Number(m.rateSats);
    // MIN — smallest redeem whose output clears dust after miner fee:
    //   units × rate − miner ≥ dust  ⇒  units ≥ ceil((dust+miner)/rate)
    const minUnits = Math.max(1, Math.ceil((DUST_BITS + MINER_FEE_BITS) / rate));
    // MAX — the user's whole MBA balance. Redeems don't consume any BCH
    // beyond the miner fee, which comes out of the released backing, so
    // no discount needed.
    const balUnits = Math.floor(balFrom);
    const maxUnits = Math.max(0, balUnits);
    // MED — geometric mean.
    const medUnits = minUnits > 0 && maxUnits > minUnits
      ? Math.floor(Math.sqrt(minUnits * maxUnits))
      : Math.floor(maxUnits / 2);
    return { min: minUnits, med: medUnits, max: maxUnits, kind: 'redeem' };
  }
  return { min: 0, med: balFrom / 2, max: balFrom, kind: 'unknown' };
};

(window as unknown as { potidaeaEstimateNet?: (from: string, to: string, amt: number) => SwapNetEstimate | null }).potidaeaEstimateNet = function(from: string, to: string, amt: number): SwapNetEstimate | null {
  if (!(amt > 0)) return null;
  // Claim: BCH → known MBA
  if (from === 'BCH' && to in KNOWN_MBAS) {
    const m = KNOWN_MBAS[to];
    const grossBits = Math.floor(amt * 1e8);
    const units = Math.floor(grossBits / Number(m.rateSats));
    // Founder fee is deducted from the wallet's BCH change (extra output).
    const founderFeeBits = Math.floor(grossBits * Number(m.founderFeeBps) / 10000);
    // Platform fee: 0.5 % (posture A, PLATFORM_FEE_V1).
    const platformFeeBits = Math.floor(grossBits * 50 / 10000);
    const feeBch = (founderFeeBits + platformFeeBits) / 1e8;
    return { net: units, kind: 'claim', feeBch, totalCostBch: amt + feeBch };
  }
  // Redeem: known MBA → BCH
  if (to === 'BCH' && from in KNOWN_MBAS) {
    const m = KNOWN_MBAS[from];
    const units = Math.floor(amt);
    const releasedBits = units * Number(m.rateSats);
    // ~3000 bits miner fee, negligible on typical sizes but visible on
    // sub-dust redeems — subtract to be honest.
    const outputBits = Math.max(0, releasedBits - 3000);
    return { net: outputBits / 1e8, kind: 'redeem', feeBch: 0, totalCostBch: 0 };
  }
  return { net: 0, kind: 'unknown', feeBch: 0, totalCostBch: 0 };
};

// Console helpers for the dev-signer mode (localhost only — see signer-store.ts).
// Use: window.potidaeaDev.pinWif('cRA1KX…'); location.reload();
(window as unknown as { potidaeaDev: unknown }).potidaeaDev = {
  pinWif: pinDevWif,
  clear: clearDevWif,
  get signer() { return localSigner; },
};

// ── Cold-visit welcome overlay ─────────────────────────────────────────
// Shown on #v-swap when there's no signer AND the visitor hasn't dismissed
// for the session. The wallet chip's "no wallet attached" state is easy to
// miss on first load, and the swap panels stay half-empty until a wallet
// attaches — so a full-card welcome guides the visitor to the right action
// (Create · Import · Connect). Click any CTA → opens the same modal the
// wallet-menu items open, so no dedicated backing code path.
//
// Dismissal is per-session (sessionStorage, gone on tab close) so a user
// who wants to look around first isn't nagged, but a fresh visit still
// gets the intro.
const WELCOME_DISMISS_KEY = 'deviant.potidaea.welcome-dismissed.v1';

function shouldShowWelcome(): boolean {
  if (localSigner) return false;
  try { if (sessionStorage.getItem(WELCOME_DISMISS_KEY) === '1') return false; } catch { /* noop */ }
  return true;
}

function paintWelcomeOverlay(): void {
  const overlay = document.getElementById('welcomeOverlay');
  const view = document.getElementById('v-swap');
  if (!overlay || !view) return;
  const showing = shouldShowWelcome();
  overlay.hidden = !showing;
  view.classList.toggle('v-swap-welcome-mode', showing);
  if (!showing) return;
  // Only rebuild when actually shown — cheaper on the common case where a
  // signer is already attached at boot.
  overlay.innerHTML =
    '<div class="welcome-card">' +
      '<div class="welcome-eyebrow">Potidaea · Orbital Swap · chipnet</div>' +
      '<div class="welcome-title">Trade BCH ↔ backed assets, counterparty-free</div>' +
      '<div class="welcome-sub">' +
        'Every swap runs through an on-chain covenant at a fixed rate — no order book, no LP, no counterparty. ' +
        '<b>Attach a wallet</b> to start; nothing is trusted or custodial.' +
      '</div>' +
      '<div class="welcome-actions">' +
        '<div class="welcome-btn primary" data-welcome-act="create">' +
          '<span class="welcome-ico">＋</span>' +
          '<span class="welcome-lbl">Create a wallet</span>' +
          '<span class="welcome-hint">fresh chipnet key · stays in this browser</span>' +
        '</div>' +
        '<div class="welcome-btn" data-welcome-act="import">' +
          '<span class="welcome-ico">↧</span>' +
          '<span class="welcome-lbl">Import a WIF</span>' +
          '<span class="welcome-hint">paste an existing chipnet private key</span>' +
        '</div>' +
        '<div class="welcome-btn" data-welcome-act="wizard">' +
          '<span class="welcome-ico">⌘</span>' +
          '<span class="welcome-lbl">Connect a wallet</span>' +
          '<span class="welcome-hint">pair via WizardConnect · Nostr transport</span>' +
        '</div>' +
      '</div>' +
      '<div class="welcome-foot">' +
        'Not ready? ' +
        '<a data-welcome-act="dismiss">Look around first →</a>' +
      '</div>' +
    '</div>';
  overlay.querySelectorAll<HTMLElement>('[data-welcome-act]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const act = btn.getAttribute('data-welcome-act');
      if (act === 'create') openCreateModal();
      else if (act === 'import') openImportModal();
      else if (act === 'wizard') wc.connect();
      else if (act === 'dismiss') {
        try { sessionStorage.setItem(WELCOME_DISMISS_KEY, '1'); } catch { /* noop */ }
        paintWelcomeOverlay();
      }
    });
  });
}

(window as unknown as { paintWelcomeOverlay?: () => void }).paintWelcomeOverlay = paintWelcomeOverlay;

// ── Wallet menu — repaint the mockup's own #walletMenu ─────────────────────
// The mockup already has a coordinated pair of header dropdowns (#netMenu +
// #walletMenu) with mutual-exclusion (opening one closes the other) and
// outside-click dismissal. We repaint #walletMenu's contents rather than
// float our own menu — that way the mockup's toggle / closeAll / outside-
// click logic works with our items unchanged.

function paintWalletMenu(): void {
  const menu = document.getElementById('walletMenu');
  if (!menu) return;

  const items: string[] = [];
  const stored = listUserWallets();
  const wcAddr = (wc.snapshot.phase === 'paired' && wc.snapshot.cashaddr) ? wc.snapshot.cashaddr : null;

  // Section 1 — every stored user wallet, active first (marked with a
  // filled dot). Non-active rows are switchable in one click; each row has
  // an ✕ that removes just that wallet from storage without touching the
  // rest. When the active row is removed, the next stored wallet takes
  // over automatically (or the store empties).
  if (stored.length || wcAddr) {
    items.push('<div class="hm-head">Your wallets</div>');
    for (const w of stored) {
      const short = w.cashaddr.slice(0, 14) + '…' + w.cashaddr.slice(-6);
      items.push(
        '<div class="hm-item ' + (w.isActive ? 'on' : '') + '" data-act="' + (w.isActive ? 'noop' : 'switch') + '" data-addr="' + escAttr(w.cashaddr) + '" title="' + escAttr(w.cashaddr) + '">' +
          '<span class="hm-dot ' + (w.isActive ? 'on' : '') + '"></span>' +
          '<span class="hm-mono">' + short + '</span>' +
          '<span class="hm-tag">' + escHtml(w.label) + '</span>' +
          '<button class="hm-x" data-act="rename" data-addr="' + escAttr(w.cashaddr) + '" data-label="' + escAttr(w.label) + '" title="Rename this wallet">✎</button>' +
          '<button class="hm-x" data-act="copy"   data-addr="' + escAttr(w.cashaddr) + '" title="Copy address to clipboard">⧉</button>' +
          '<button class="hm-x" data-act="remove" data-addr="' + escAttr(w.cashaddr) + '" title="Remove this wallet from storage">✕</button>' +
        '</div>',
      );
    }
    if (wcAddr) {
      const short = wcAddr.slice(0, 14) + '…' + wcAddr.slice(-6);
      const isActiveWc = !localSigner;   // WC wins only when no local signer is active
      items.push(
        '<div class="hm-item ' + (isActiveWc ? 'on' : '') + '" title="' + escAttr(wcAddr) + '">' +
          '<span class="hm-dot ' + (isActiveWc ? 'on' : '') + '"></span>' +
          '<span class="hm-mono">' + short + '</span>' +
          '<span class="hm-tag">WC</span>' +
          '<button class="hm-x" data-act="copy" data-addr="' + escAttr(wcAddr) + '" title="Copy address to clipboard">⧉</button>' +
          '<button class="hm-x" data-act="disconnect" title="Unpair the WalletConnect session">✕</button>' +
        '</div>',
      );
    }
    // Dev-mode signer, if active but not tracked in the user store
    if (localSigner?.mode === 'dev') {
      const addr = localSigner.cashaddr;
      const short = addr.slice(0, 14) + '…' + addr.slice(-6);
      items.push(
        '<div class="hm-item on" title="' + escAttr(addr) + ' (dev signer · localhost only)">' +
          '<span class="hm-dot on"></span>' +
          '<span class="hm-mono">' + short + '</span>' +
          '<span class="hm-tag">dev</span>' +
          '<button class="hm-x" data-act="copy" data-addr="' + escAttr(addr) + '" title="Copy address to clipboard">⧉</button>' +
          '<button class="hm-x" data-act="detach-dev" title="Clear dev signer">✕</button>' +
        '</div>',
      );
    }
    items.push('<div class="hm-sep"></div>');
  }

  // Section 2 — three ways to attach a new wallet. Order per request.
  items.push(
    '<div class="hm-head">Attach another</div>',
    '<div class="hm-item" data-act="create">＋ Create a wallet</div>',
    '<div class="hm-item" data-act="import">↧ Import a wallet</div>',
    '<div class="hm-item" data-act="wizard">⌘ Connect a wallet</div>',
  );

  // Section 3 — session maintenance. "Reset local assets" wipes this
  // browser's memory of MBAs the user created or that Katalogos discovery
  // half-populated; on-chain state is untouched. Useful after Artemision
  // test runs pile the picker with orphan tickers.
  items.push(
    '<div class="hm-sep"></div>',
    '<div class="hm-head">Session</div>',
    '<div class="hm-item" data-act="reset-assets">↺ Reset local assets<span class="hm-sub">drop cached MBAs · on-chain untouched</span></div>',
  );

  menu.innerHTML = items.join('');
  menu.classList.add('wide');

  // Wire clicks. Any click inside the menu must stopPropagation so the
  // mockup's document-level closeAll doesn't double-fire. The X buttons
  // must also stopPropagation before their row's own handler runs (they
  // live INSIDE the row div, so a click on ✕ would bubble to the row and
  // trigger a switch too — the inner handler catches it first).
  menu.querySelectorAll('button.hm-x').forEach((btn) => {
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const el = btn as HTMLElement;
      const act = el.getAttribute('data-act');
      const addr = el.getAttribute('data-addr') ?? '';
      if (act === 'remove') removeStoredWallet(addr);
      else if (act === 'disconnect') wc.disconnect();
      else if (act === 'detach-dev') detachLocalSigner();
      else if (act === 'copy') copyAddressToClipboard(addr, el);
      else if (act === 'rename') runRenameWallet(addr, el.getAttribute('data-label') ?? '');
    });
  });
  menu.querySelectorAll('.hm-item[data-act]').forEach((row) => {
    row.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const el = row as HTMLElement;
      const act = el.getAttribute('data-act');
      if (act === 'switch') {
        const addr = el.getAttribute('data-addr');
        if (addr) doSwitchToWallet(addr);
        closeAllHeaderMenus();
        return;
      }
      if (act === 'noop') return;   // clicking the already-active row does nothing
      closeAllHeaderMenus();
      if (act === 'create') openCreateModal();
      else if (act === 'import') openImportModal();
      else if (act === 'wizard') wc.connect();
      else if (act === 'reset-assets') runResetLocalAssets();
    });
  });
}

function escHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c] as string));
}
function escAttr(s: string): string { return escHtml(s); }

/** Render a txid into a target element with a short display, copy button,
 *  and a chipnet-explorer link. Reused across the swap done sheet, the
 *  genesis wizard, and the publish overlay so every real broadcast has a
 *  consistent receipt shape the user can act on. */
function renderTxidWithActions(target: HTMLElement, txid: string): void {
  const short = txid.length > 20 ? (txid.slice(0, 10) + '…' + txid.slice(-6)) : txid;
  target.innerHTML =
    '<span style="font-family:var(--font-mono);color:var(--acid);word-break:break-all" title="' + escAttr(txid) + '">' + escHtml(short) + '</span>' +
    ' <button class="tx-mini-btn" data-tx-copy="' + escAttr(txid) + '" title="Copy full txid">⧉</button>' +
    ' <a class="tx-mini-btn" href="https://chipnet.imaginary.cash/tx/' + encodeURIComponent(txid) + '" target="_blank" rel="noopener noreferrer" title="View on chipnet explorer">↗</a>';
  target.querySelectorAll<HTMLButtonElement>('[data-tx-copy]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const t = btn.getAttribute('data-tx-copy') ?? '';
      try {
        await navigator.clipboard.writeText(t);
        const orig = btn.textContent;
        btn.textContent = '✓';
        setTimeout(() => { btn.textContent = orig; }, 1200);
      } catch { /* clipboard blocked — silent */ }
    });
  });
}

/** Wipe locally-cached MBAs so the swap picker resets to the canonical
 *  seed set (BCH · DEA · ASM · ASM2). On-chain state is untouched; only
 *  this browser's memory of half-configured / test genesis entries is
 *  dropped. Tx log stays — that's tied to the wallet, not the picker. */
function runResetLocalAssets(): void {
  if (!confirm(
    'Reset local assets?\n\n' +
    'This drops MBAs cached in this browser (Artemision test runs, ' +
    'Katalogos-discovered rows). On-chain state is untouched — you can ' +
    're-discover them by opening Katalogos or genesising again.',
  )) return;
  clearAllLocalAssets();
  // Purge KNOWN_MBAS down to the seed set the module ships with. We
  // rebuild by re-importing the seed keys — anything not in the seeds
  // was a merged local/discovered entry.
  const seedKeys = new Set<string>(['ASM', 'ASM2']);
  for (const k of Object.keys(KNOWN_MBAS)) {
    if (!seedKeys.has(k)) delete KNOWN_MBAS[k];
  }
  // Reload the mockup's ASSETS from what remains, then refresh every
  // dependent surface. Cheapest is a full page reload so no state slips
  // through the cracks — but avoid it when the user has an in-flight
  // signer or unsent tx (they don't here; this is a settings action).
  location.reload();
}

/** Called every time the active signer changes — import, create, switch,
 *  or WC-pair. Refresh needs a bit of hand-holding at this moment:
 *
 *    • Pending-swap overlays from any PREVIOUS wallet are meaningless for
 *      the new address; clear them so paintBalance doesn't apply the old
 *      wallet's optimistic deltas to the new one's UTXO set.
 *    • The first Electrum call after wallet-switch can be slow (fresh
 *      cashaddr subscription, plus tokenized listunspent) — a single
 *      fire-and-forget refresh can lose the race with the user opening
 *      the Balances view before it lands, leaving the page looking empty
 *      until they hit the manual refresh button. Schedule follow-up
 *      refreshes so the balances populate without user action. */
// ── Cross-device tx-log hydration ─────────────────────────────────────
// My-Transactions is stored in localStorage, so a swap made on mobile
// won't appear on desktop with the same WIF. Fix by scanning the chain
// on wallet attach: `blockchain.address.get_history(cashaddr)` returns
// every tx affecting the address, and per-tx verbose fetches let us
// classify each as a claim / redeem. Results merge into TX_LOG_KEY with
// txid-dedupe, so local overlay (pending / optimistic) and chain
// history coexist without duplicates.
//
// v1 scope: detect CLAIMS (BCH → known MBA) by finding an FT output of
// a KNOWN_MBAS category at the user's tokenaddr. Redeems are burn-in-
// place and harder to classify without input-scan (no covenant address
// touched); a v2 pass will handle them. Genesis / publish stay local-
// only for now — they're rare and the local overlay covers them.
const HYDRATE_MAX_TXS = 100;  // walk at most this many recent entries
const HYDRATE_CONCURRENCY = 4; // parallel verbose fetches
let hydrateInFlight = false;

async function hydrateTxLogFromChain(signer: Signer): Promise<void> {
  if (hydrateInFlight) return; // guard against double-fire from stacked setTimeouts
  hydrateInFlight = true;
  try {
    if (!currentClient) currentClient = await ElectrumClient.connectAny();
    const client = currentClient;
    const rawHist = await client.request<Array<{ tx_hash: string; height: number }>>(
      'blockchain.address.get_history', [signer.cashaddr],
    );
    const hist = (rawHist ?? []).slice(-HYDRATE_MAX_TXS).reverse(); // newest first

    // Decode user's PKH so we can match outputs going TO the user.
    let userPkhHex = '';
    try {
      const dec = decodeCashAddress(signer.cashaddr);
      if (typeof dec !== 'string' && dec.payload) userPkhHex = bytesToHex(dec.payload).toLowerCase();
    } catch { /* ignore — will skip classification if we can't identify user */ }
    if (!userPkhHex) return;

    // Existing local txids for dedupe.
    const existing = new Set(readTxLog().map((e) => e.txid));
    const toFetch = hist.filter((h) => !existing.has(h.tx_hash));
    if (toFetch.length === 0) return;

    // Build a categoryId → { ticker, rateSats } lookup.
    const catToMba: Record<string, { ticker: string; rateSats: bigint }> = {};
    for (const [ticker, m] of Object.entries(KNOWN_MBAS)) {
      catToMba[m.categoryId.toLowerCase()] = { ticker, rateSats: m.rateSats };
    }

    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < toFetch.length) {
        const h = toFetch[cursor++];
        try {
          const verbose = await client.request<FulcrumVerboseTx>('blockchain.transaction.get', [h.tx_hash, true]);
          const classified = classifyChainTx(verbose, userPkhHex, catToMba, h.tx_hash, h.height);
          if (classified) hydrateInsert(classified);
        } catch (e) {
          console.warn('[hydrate] verbose fetch failed for', h.tx_hash.slice(0, 10), e instanceof Error ? e.message : e);
        }
      }
    };
    const workers = Array.from({ length: HYDRATE_CONCURRENCY }, () => worker());
    await Promise.all(workers);
  } catch (e) {
    console.warn('[hydrate] chain-scan failed:', e instanceof Error ? e.message : e);
  } finally {
    hydrateInFlight = false;
  }
}

/** Classify a verbose tx as a claim / redeem for the user, or return
 *  null if we can't tell. v1: claim-only (FT of known category received
 *  at user's tokenaddr). Later: redeem via input-scan. */
function classifyChainTx(
  verbose: FulcrumVerboseTx,
  userPkhHex: string,
  catToMba: Record<string, { ticker: string; rateSats: bigint }>,
  txid: string,
  _height: number,
): Omit<TxLogEntry, 'ts'> | null {
  const outs = verbose.vout ?? [];
  // Find FT outputs at user's PKH matching a known category. We match by
  // "does the scriptPubKey.hex include the user's PKH" — that catches both
  // plain P2PKH (76a914<pkh>88ac) and the p2pkh-with-tokens variant, which
  // share the same 20-byte pkh.
  const ftReceived = new Map<string, bigint>(); // categoryId → amount received by user
  let bchReceivedToUser = 0n;
  for (const v of outs) {
    const spk = (v.scriptPubKey?.hex ?? '').toLowerCase();
    if (!spk.includes(userPkhHex)) continue; // not going to user
    const td = v.token_data ?? v.tokenData;
    if (td && td.category) {
      const cat = td.category.toLowerCase();
      if (catToMba[cat]) {
        const prev = ftReceived.get(cat) ?? 0n;
        try { ftReceived.set(cat, prev + BigInt(td.amount ?? '0')); } catch { /* noop */ }
      }
    } else {
      try { bchReceivedToUser += BigInt(Math.floor((v.value ?? 0))); } catch { /* noop */ }
    }
  }

  // CLAIM: user received FT of a known category. Build the log entry.
  if (ftReceived.size > 0) {
    // Take the largest FT delta as the primary claim (a single swap
    // touches one category at a time).
    let bestCat = '', bestAmount = 0n;
    for (const [c, a] of ftReceived) if (a > bestAmount) { bestAmount = a; bestCat = c; }
    const m = catToMba[bestCat];
    const bchIn = (bestAmount * m.rateSats);
    const bchStr = (Number(bchIn) / 1e8).toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
    return {
      kind: 'claim',
      ticker: m.ticker,
      fromSym: 'BCH',
      toSym: m.ticker,
      fromAmount: bchStr + ' BCH',
      toAmount: String(bestAmount) + ' ' + m.ticker,
      amount: String(bestAmount) + ' ' + m.ticker + ' received',
      txid,
      // Use block time when the tx has one — Fulcrum returns it as `time`
      // or `blocktime` depending on version. Falls back to now (surfaces
      // as "just now" in the log). logTx replaces ts inside, so we bypass
      // it here by casting; the shipped entry gets a real ts from the
      // block header if available.
      note: verbose.time
        ? 'chain-hydrated ts=' + verbose.time
        : (verbose.blocktime ? 'chain-hydrated ts=' + verbose.blocktime : 'chain-hydrated'),
    } as Omit<TxLogEntry, 'ts'>;
  }

  // REDEEM detection is v2 — needs input-scan (fetch each vin's prev tx
  // to see if the user was spending a known-category FT). Skip for now.
  void bchReceivedToUser;
  return null;
}

/** Insert a chain-hydrated entry directly into localStorage, using the
 *  block time from the tx (not Date.now()) so the log stays ordered by
 *  real chain time. Dedupes by txid: if the row exists (e.g. from an
 *  interactive broadcast) we leave it alone. Repaints the panel. */
function hydrateInsert(entry: Omit<TxLogEntry, 'ts'>): void {
  const list = readTxLog();
  if (list.some((e) => e.txid === entry.txid)) return; // dedupe
  const m = entry.note && /chain-hydrated\s+ts=(\d+)/i.exec(entry.note);
  const chainTs = m ? Number(m[1]) * 1000 : Date.now();
  const ts = Number.isFinite(chainTs) && chainTs > 0 ? chainTs : Date.now();
  list.push({ ts, ...entry });
  list.sort((a, b) => (b.ts || 0) - (a.ts || 0));
  try { localStorage.setItem(TX_LOG_KEY, JSON.stringify(list.slice(0, TX_LOG_MAX))); } catch { /* noop */ }
  paintOdTxLog();
}

function onSignerChanged(): void {
  // Drop any pending-swap overlays — they were keyed to the previous
  // wallet's UTXO snapshot and would mis-apply here.
  pendingSwaps.length = 0;
  savePendingSwapsToStorage();
  void refreshBalance();
  // Retry a few times: Electrum's first response after wallet-switch can
  // be seconds slow on a cold cashaddr subscription.
  setTimeout(() => void refreshBalance(), 700);
  setTimeout(() => void refreshBalance(), 2500);
  setTimeout(() => void refreshBalance(), 6000);
  // Wallet attach/detach flips the welcome overlay: on attach we hide it
  // (a fresh visitor just paired), on detach we surface it again (they
  // popped the only wallet — nothing to trade with now).
  try { paintWelcomeOverlay(); } catch { /* noop */ }
  try { paintMyAssets(); } catch { /* noop */ }
  // Cross-device tx-log hydration — pulls historical swaps from chipnet
  // so a swap made on mobile appears on desktop with the same WIF.
  // Delayed 300ms so the balance refresh grabs the RPC slot first
  // (users care more about seeing their balance than the tx-log
  // instantly). Failures are logged and silently ignored — the local
  // overlay + interactive broadcasts still populate the log normally.
  if (localSigner) {
    const s = localSigner;
    setTimeout(() => void hydrateTxLogFromChain(s), 300);
  }
}

function doSwitchToWallet(cashaddr: string): void {
  const s = switchUserWallet(cashaddr);
  if (!s) return;
  localSigner = s;
  renderChip();
  paintWalletMenu();
  onSignerChanged();
}

function removeStoredWallet(cashaddr: string): void {
  // Nice to warn before deleting the ACTIVE one (it changes what the app
  // is talking to), but a non-active wallet is a low-stakes remove; still
  // confirm because a WIF loss is unrecoverable.
  const isActive = !!localSigner && localSigner.cashaddr === cashaddr;
  const msg = isActive
    ? 'Remove this active wallet? Its WIF stored in this browser will be cleared. If you have not backed it up, funds at this address are unrecoverable.'
    : 'Remove this wallet from storage? Its WIF will be cleared. If you have not backed it up, funds at this address are unrecoverable.';
  if (!confirm(msg)) return;
  const next = removeUserWallet(cashaddr);
  if (isActive) {
    localSigner = next;
    renderChip();
    if (next) void refreshBalance();
    else zeroAllBalances();
  }
  paintWalletMenu();
}

function copyAddressToClipboard(cashaddr: string, btn: HTMLElement): void {
  const write = (): Promise<void> =>
    (navigator.clipboard && navigator.clipboard.writeText)
      ? navigator.clipboard.writeText(cashaddr)
      // Fallback for older browsers / non-HTTPS contexts — build a hidden
      // textarea, select, execCommand.
      : new Promise<void>((resolve, reject) => {
          try {
            const ta = document.createElement('textarea');
            ta.value = cashaddr;
            ta.style.cssText = 'position:fixed;left:-9999px;top:0;';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            ta.remove();
            resolve();
          } catch (e) { reject(e); }
        });
  void write().then(() => {
    // Visible confirmation — swap the icon text briefly, restore after 1.2s.
    const original = btn.textContent;
    btn.textContent = '✓';
    setTimeout(() => { btn.textContent = original; }, 1200);
  }).catch((e) => {
    console.warn('[wallet] copy failed:', e instanceof Error ? e.message : e);
  });
}

function runRenameWallet(cashaddr: string, currentLabel: string): void {
  const next = window.prompt('New label for this wallet:', currentLabel);
  if (next == null) return;                 // user cancelled
  const cleaned = next.trim();
  if (!cleaned) return;                     // empty — keep existing
  if (cleaned === currentLabel) return;     // no change
  const ok = renameUserWallet(cashaddr, cleaned);
  if (!ok) { console.warn('[wallet] rename failed for', cashaddr); return; }
  paintWalletMenu();
}

function closeAllHeaderMenus(): void {
  document.getElementById('walletMenu')?.classList.remove('open');
  document.getElementById('netMenu')?.classList.remove('open');
}

function openImportModal(): void {
  const el = document.createElement('div');
  el.className = 'wc-scrim'; el.id = 'wc-import';
  el.innerHTML =
    '<div class="wc-modal">' +
      '<div class="wc-modal-h">Import WIF</div>' +
      '<p class="wc-modal-sub">Paste a WIF-encoded private key. It stays in this browser (localStorage) — nothing is uploaded.</p>' +
      '<div class="wc-warn">⚠ The page holds the key while attached. Use a key you\'re willing to expose to the browser — not one guarding significant funds.</div>' +
      '<label class="wc-label">WIF <input id="wc-imp-wif" type="password" autocomplete="off" spellcheck="false" placeholder="c… or K/L…" /></label>' +
      '<div id="wc-imp-err" class="wc-err" hidden></div>' +
      '<div class="wc-actions">' +
        '<button class="wc-primary" id="wc-imp-go">Attach</button>' +
        '<button class="wc-cancel">Cancel</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(el);
  const close = (): void => el.remove();
  el.addEventListener('click', (ev) => { if (ev.target === el) close(); });
  el.querySelector('.wc-cancel')?.addEventListener('click', close);
  el.querySelector('#wc-imp-go')?.addEventListener('click', () => {
    const input = el.querySelector('#wc-imp-wif') as HTMLInputElement;
    const err = el.querySelector('#wc-imp-err') as HTMLElement;
    const wif = input.value.trim();
    if (!wif) { err.textContent = 'WIF required'; err.hidden = false; return; }
    const s = importUserWif(wif);
    if (!s) { err.textContent = 'Invalid WIF — check for typos'; err.hidden = false; return; }
    if (!s.cashaddr.startsWith('bchtest:')) {
      clearUserWallet();
      err.textContent = 'WIF network does not match the network selected above (address ' + s.cashaddr.slice(0, 10) + '…).';
      err.hidden = false;
      return;
    }
    localSigner = s;
    close();
    renderChip();
    paintWalletMenu();
    onSignerChanged();
  });
  (el.querySelector('#wc-imp-wif') as HTMLInputElement | null)?.focus();
}

function openCreateModal(): void {
  const res = createUserWallet();
  const wif = res?.wif ?? '';
  const addr = res?.signer.cashaddr ?? '(create failed)';
  const el = document.createElement('div');
  el.className = 'wc-scrim'; el.id = 'wc-create';
  el.innerHTML =
    '<div class="wc-modal">' +
      '<div class="wc-modal-h">New wallet</div>' +
      '<p class="wc-modal-sub">A fresh key was generated for this browser. It is stored in localStorage. There is no seed phrase and no recovery — clear browser data and the key is gone.</p>' +
      '<div class="wc-warn">⚠ The page holds the key while attached. Back up the WIF now if you want to keep this address.</div>' +
      '<label class="wc-label">Address<code class="wc-code" id="wc-cre-addr">' + escapeHtml(addr) + '</code></label>' +
      '<label class="wc-label">WIF (back this up now if you want it) ' +
        '<span class="wc-reveal-row">' +
          '<code class="wc-code wc-mask" id="wc-cre-wif" data-wif="' + escapeAttr(wif) + '">••••••••••••••••••••••••••••••••••••••••••••••••••</code>' +
          '<button class="wc-mini" id="wc-cre-show">Show</button>' +
          '<button class="wc-mini" id="wc-cre-copy">Copy</button>' +
        '</span>' +
      '</label>' +
      '<p class="wc-modal-sub">Fund this address to run swaps.</p>' +
      '<div class="wc-actions">' +
        '<button class="wc-primary" id="wc-cre-ok">Done</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(el);
  const close = (): void => el.remove();
  el.addEventListener('click', (ev) => { if (ev.target === el) close(); });
  el.querySelector('#wc-cre-ok')?.addEventListener('click', () => {
    close();
    if (res) { localSigner = res.signer; renderChip(); paintWalletMenu(); onSignerChanged(); }
  });
  el.querySelector('#wc-cre-show')?.addEventListener('click', () => {
    const codeEl = el.querySelector('#wc-cre-wif') as HTMLElement;
    codeEl.textContent = wif;
    codeEl.classList.remove('wc-mask');
  });
  el.querySelector('#wc-cre-copy')?.addEventListener('click', async () => {
    try { await navigator.clipboard.writeText(wif); } catch { /* clipboard blocked */ }
    const btn = el.querySelector('#wc-cre-copy') as HTMLButtonElement;
    btn.textContent = 'Copied'; setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c] as string));
}
function escapeAttr(s: string): string { return escapeHtml(s); }

// Expose the reveal helper so a paranoid user can peek at their WIF later
// via the console (or from a "Show WIF" button we might add to Settings).
(window as unknown as { potidaeaWallet: unknown }).potidaeaWallet = {
  revealWif: () => readUserWif(),
  clear: () => { clearUserWallet(); localSigner = null; renderChip(); },
  get signer() { return localSigner; },
};

// Console API for Share MBA genesis — the wizard is Artemision-only for
// now, so this is the (developer-visible) way to mint + publish a Share
// asset. Once the wizard grows a template picker this becomes the fast
// path for testing.
async function genesisShareViaConsole(input: {
  ticker: string;
  rateSats: number | bigint;
  feeBps: number | bigint;
  companySplitBps: number | bigint;
  companyLockingBytecodeHex?: string;      // defaults to signer's own P2PKH
  distributionLockingBytecodeHex?: string; // defaults to signer's own P2PKH
  name?: string; glyph?: string;
}): Promise<ShareGenesisResult> {
  if (!localSigner) throw new Error('attach a wallet first');
  // Default both LB fields to the signer's own P2PKH so a solo dev can smoke
  // the full loop without staging separate keys for company / distribution.
  const ownLB = pkhToP2PKHLBHex(localSigner.publicKeyHash);
  const params: ShareGenesisParams = {
    ticker: input.ticker,
    rateSats: BigInt(input.rateSats),
    feeBps: BigInt(input.feeBps),
    companySplitBps: BigInt(input.companySplitBps),
    companyLockingBytecodeHex: input.companyLockingBytecodeHex ?? ownLB,
    distributionLockingBytecodeHex: input.distributionLockingBytecodeHex ?? ownLB,
    name: input.name,
    glyph: input.glyph,
  };
  const res = await genesisDeviantShareMBA(localSigner, params);
  // Rehydrate ASSETS + KNOWN_MBAS so the new Share MBA is swappable
  // immediately in this session.
  hydrateLocalMbas();
  filterAssetsToSwappable();
  try { window.recalc?.(); } catch { /* noop */ }
  try { window.renderKat?.(); } catch { /* noop */ }
  return res;
}

function pkhToP2PKHLBHex(pkh: Uint8Array): string {
  const lb = new Uint8Array(25);
  lb[0] = 0x76; lb[1] = 0xa9; lb[2] = 0x14; lb.set(pkh, 3); lb[23] = 0x88; lb[24] = 0xac;
  let s = ''; for (const b of lb) s += b.toString(16).padStart(2, '0'); return s;
}

/** Decode a cashaddr (bchtest: or its token-aware sibling) → 25-byte P2PKH
 *  locking bytecode hex. Returns null on any decode failure so the caller
 *  can surface a friendly error to the wizard user. */
function cashaddrToP2PKHLBHex(cashaddr: string): string | null {
  try {
    const dec = decodeCashAddress(cashaddr.trim());
    if (typeof dec === 'string') return null;
    // decodeCashAddress returns { prefix, type, payload } — payload is the
    // 20-byte hash160 regardless of whether the address is token-aware.
    if (!dec.payload || dec.payload.length !== 20) return null;
    return pkhToP2PKHLBHex(dec.payload);
  } catch { return null; }
}

(window as unknown as { potidaeaShare: unknown }).potidaeaShare = {
  genesis: genesisShareViaConsole,
  // Convenience: publish and end-to-end publish helpers reuse the existing
  // openPublishOverlay path; a Share ticker in KNOWN_MBAS gets the right
  // DVMB blob via runRealPublish (which reads templateId off the row).
};
